import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { lastValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, catchError, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { CCMCConfirmDialogComponent } from 'src/@ccmc/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from 'src/@ccmc/components/error-dialog/error-dialog.component';
import { AssetService } from 'src/@ccmc/services/asset.service';
import { CcmcApiService } from 'src/@ccmc/services/ccmc-api.service';
import { DocumentConnectorService } from 'src/@ccmc/services/doc-connector.service';
import { CCMCSelectedFieldService } from 'src/@ccmc/services/selected-field.service';
import { SpinnerService } from 'src/@ccmc/services/spinner.service';
import { AmplifyService } from 'src/@ccmc/services/amplify.service';
import {
  ACTIVITY_GRACE_MS,
  JUST_RUN_WINDOW_MS,
  MAX_IN_PROGRESS_MS,
  DETAIL_BENIGN_FRAGMENTS,
  DETAIL_ERROR_FRAGMENTS,
  STATUS_MESSAGES,
  STATUS_VALUES,
  TIME_THRESHOLD_MS,
  TRAILING_DOCS_STATUS_SUFFIX,
  TRANSFER_FAILURE_FRAGMENTS
} from './constants';
import * as trailingDocsUtil from './trailing-docs.util';
import { DialogHandlerService } from 'src/@ccmc/services/dialogHandler.service';
import {
  ResendDocConnectorDialogComponent
} from 'src/@ccmc/components/resend-doc-connector-dialog/resend-doc-connector-dialog.component';
import { environment } from 'src/environments/environment';
import { CustomErrorHandlerService } from 'src/@ccmc/services/custom-error-handler.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Buffer } from 'buffer';
import { SuccessDialogComponent } from 'src/@ccmc/components/success-dialog/success-dialog.component';
import { DocConnectorHistoryResponse } from './doc-connector-history-response.model';

@Component({
  selector: 'app-doc-connector-history',
  templateUrl: './doc-connector-history.component.html',
  styleUrls: ['./doc-connector-history.component.scss']
})
export class DocConnectorHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  dcDoc: any;
  constructor(
    private dcService: DocumentConnectorService,
    private assetService: AssetService,
    private dialog: MatDialog,
    private selectedFieldService: CCMCSelectedFieldService,
    private spinnerService: SpinnerService,
    private ccmcApiService: CcmcApiService,
    private amplifyService: AmplifyService,
    private dialogHandlerService: DialogHandlerService,
    private customErrorHandlerService: CustomErrorHandlerService,
    private httpClient: HttpClient,
    private http: HttpClient,
    private cd: ChangeDetectorRef
  ) { }

  filterSearchText: string;
  // dataSourceChangepage: any;
  selectedLoan: any;
  assetIdSettingsSubscription: Subscription;
  settings: Array<any> = [];
  private dcDocSub: Subscription;
  private documentLosSub: Subscription;
  private selectedSub: Subscription;
  private spinnerSub: Subscription;
  showSpinner: boolean;
  history: any = [];
  unsubscribe: Subject<any> = new Subject();
  displayedColumns = ['checkBox', 'loanNumber', 'date', 'status', 'processed', 'transmitted', 'pending', 'failed'];
  selectedLoanDisplayedColumns = [
    'fileName',
    'date',
    'statusCompressedandMerged',
    'statusCore',
    'statusS3'
  ];

  // Filter search subject for debouncing
  private filterSubject = new Subject<string>();
  private lastApiSearchTerm = '';
  private isSearching = false;

  // Search result caching
  private searchResultsCache: Map<string, any[]> = new Map<string, any[]>();
  private readonly MAX_SEARCH_RESULTS = 500;
  searchLimitReached = false;

  private allHistoryItems: any[] = []; 
  totalLoaded = 0;                      
  private currentPageIndex = 0;
  currentPageSize = 25;  
  private lastFilter = '';
  isEndOfResults = false;
  isFilteringData = false;  
  isSortingData = false;
  @ViewChild('container', { static: true })
  containerRef!: ElementRef;
  @ViewChild('loadMoreTrigger', { static: false })
  loadMoreTrigger!: ElementRef;
  dataSource = new MatTableDataSource<any>([]);
  selectedLoanDataSource: any;
  checkedLoans: any;
  
  // Separate pagination keys for normal browsing vs search/filter
  lastEvaluatedKey: any = undefined; // For normal pagination
  searchLastEvaluatedKey: any = undefined; // For search/filter pagination
  
  // Fetch-to-end functionality
  isFetchingToEnd = false;
  private cancelFetchToEnd = false;
  readonly FETCH_TO_END_BATCH_LIMIT = 1000;
  showMoreRecordsAvailable = false;
  
  // Periodic fetch - separate cache for new records
  private pendingNewRecords: any[] = [];
  hasNewRecordsAvailable = false;

  /**
   * Number of records the background poll reads per cycle.
   *
   * The poll exists to notice recent activity, so it only needs the newest slice
   * of the index. Without a bound the query reads the entire asset partition up
   * to DynamoDB's 1MB page limit every cycle, for every open tab.
   */
  private readonly POLL_PAGE_SIZE = 50;

  /**
   * Refresh interval while no loan is transferring. Finished records do not
   * change, so there is nothing to gain from reading more often than this.
   */
  private readonly POLL_IDLE_MS = 10000;

  /**
   * Refresh interval while at least one loan is still transferring, so per-file
   * progress and the final status appear without the user reloading. Bounded by
   * POLL_PAGE_SIZE, and it reverts to the idle rate as soon as work finishes or
   * a stalled record ages past TIME_THRESHOLD_MS.
   */
  private readonly POLL_ACTIVE_MS = 3000;

  /**
   * How long a tab may sit untouched before polling pauses.
   */
  private readonly INACTIVITY_LIMIT_MS = 5 * 60 * 1000;

  /**
   * How long a run may sit at Queued with nothing returned from Encompass before it
   * is reported as a retrieval failure rather than as still waiting.
   *
   * Queued is inferred from the absence of file statuses, so a run whose document
   * retrieval failed outright looks identical to one that was just accepted. Past
   * this point the distinction is no longer ambiguous: no documents are coming.
   */
  private readonly QUEUED_RETRIEVAL_TIMEOUT_MS = 15 * 60 * 1000;

  /**
   * How long to keep polling at the active rate after a loan is submitted.
   *
   * A freshly submitted loan has no in-flight files yet, so nothing would trigger
   * the active rate and the move to Queued could take a full idle interval to
   * appear. This covers the gap between the request being accepted and the
   * backend writing its first status.
   */
  private readonly POST_SUBMIT_WATCH_MS = 90 * 1000;

  /**
   * When the most recent submission was observed, or undefined if none.
   */
  private lastSubmissionAt?: number;

  /**
   * Loans submitted from this session, keyed by loanGUID, with the time of
   * submission.
   *
   * A resend does not clear the previous run's file statuses, so a loan that
   * succeeded last time keeps reporting Successful until the backend overwrites
   * them. This lets the row show Queued straight away instead of displaying the
   * previous outcome for as long as the backend takes to write.
   */
  private submittedLoans = new Map<string, number>();
  
  exportButtonSwitch: boolean = false;
  getHistoryResponse: any;
  @ViewChild('filterSearch') filterSearchRef: ElementRef;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  private selectedLoansToSendSub: Subscription;
  isFetchingHistory = false;
  /** Handle for the self-rescheduling history poll. Cleared in ngOnDestroy. */
  private intervalId: any; 
  /**
   * Set in ngOnDestroy so work already in flight can stop before it touches state
   * shared with the instance that replaced this one.
   */
  private isDestroyed = false;
  private lastActivityTime = Date.now();
  private isNXTSoftUser = false;
  isLoanSelectVisible = false;
  checkedFiles: Map<string, any> = new Map<string, any>();
  isAllSelectLoanBoxesChecked : boolean = false;
  configurations: any = {};
  noDocConnectorHistory: boolean = false;
  showSelectedLoanView: boolean = false;
  httpOptions = {
    headers: new HttpHeaders({
    "Content-Type": "application/json",
    })
  };

  ngOnInit(): void {
    // Initialize search result cache
    this.searchResultsCache = new Map<string, any[]>();
    
    this.getData();
    this.validateNXTSoftUser();
    this.loadSettings();
    this.setupFilterDebounce();

    // Watch closely just after a send so the loan visibly reaches Queued. The
    // timestamp is checked because this replays its last value on navigation.
    this.dcService.loanSubmitted
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(submission => {
        if (submission && (Date.now() - submission.at) <= this.POST_SUBMIT_WATCH_MS) {
          console.log(`Loan ${submission.loanGUID} was just submitted. Watching for updates.`);
          this.lastSubmissionAt = submission.at;
          this.submittedLoans.set(submission.loanGUID, submission.at);
        }
      });

    // Poll almost immediately after a send rather than waiting a full interval,
    // so the first backend update is picked up as soon as it lands.
    this.scheduleNextPoll(this.lastSubmissionAt !== undefined ? 500 : undefined);

    // Add event listeners for user activity
    window.addEventListener("mousemove", this.resetActivity.bind(this));
    window.addEventListener("keydown", this.resetActivity.bind(this));
    window.addEventListener("visibilitychange", this.resetActivity.bind(this));
    console.log('Doc Connector Document =>', this.dcDoc);
  }
  
  /**
   * Whether any loaded record is still transferring.
   *
   * Drives how often the table refreshes: an active run is worth watching
   * closely, whereas a table of finished loans is not going to change.
   *
   * @returns True when at least one cached or pending record is in progress.
   */
  private hasWorkInProgress(): boolean {
    // A just-submitted loan has no in-flight files yet, so watch closely until the
    // backend reports something rather than waiting out an idle interval.
    if (this.lastSubmissionAt !== undefined
        && (Date.now() - this.lastSubmissionAt) <= this.POST_SUBMIT_WATCH_MS) {
      return true;
    }

    return this.allHistoryItems.some((item: any) => this.isInProgress(item))
      || this.pendingNewRecords.some((item: any) => this.isInProgress(item));
  }

  /**
   * Queues the next history poll.
   *
   * The delay is chosen per cycle rather than fixed, so the table keeps up with
   * a live transfer without paying for that rate while nothing is happening.
   * Rescheduling always happens, even when a cycle fails, so one bad response
   * cannot leave the table permanently stale.
   */
  private scheduleNextPoll(delayOverride?: number): void {
    // A cycle that was already running when the view was destroyed still reaches the
    // finally below, and rescheduling from there would create a timer after
    // ngOnDestroy had cleared the only handle it knew about, leaving the instance
    // polling for the lifetime of the tab.
    if (this.isDestroyed) {
      return;
    }

    const delay = delayOverride
      ?? (this.hasWorkInProgress() ? this.POLL_ACTIVE_MS : this.POLL_IDLE_MS);

    this.intervalId = setTimeout(async () => {
      try {
        await this.runPollCycle();
      } catch (error) {
        console.error("Error during periodic history fetch:", error);
      } finally {
        this.scheduleNextPoll();
      }
    }, delay);
  }

  /**
   * Runs one history refresh, merging new and updated records into the cache.
   *
   * Skipped while the user is filtering, while another fetch is running, when
   * the tab is hidden, and after a period of inactivity, so a background tab
   * does not keep reading DynamoDB.
   */
  private async runPollCycle(): Promise<void> {
    // Skip periodic fetch if user is filtering data
    const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;

    if (this.isFetchingHistory ||
        isFiltering ||  // Don't auto-refresh when filtering
        document.visibilityState !== "visible" ||
        Date.now() - this.lastActivityTime >= this.INACTIVITY_LIMIT_MS ||
        this.noDocConnectorHistory !== false) {
      return;
    }

    console.log("Periodic fetch: checking for new records...");
    const response = await this.dcService.getMoreDocConnectorHistory(
      this.assetService.getSelectedAssetId(),
      undefined,  // Start from the beginning
      this.POLL_PAGE_SIZE  // Bounded page: the poll only needs recent activity
    ) as any;

    // The read above outlives ngOnDestroy when the user navigates mid-cycle, and
    // everything below mutates component state and republishes the open loan. Once
    // the view is gone this instance must stop rather than compete with the one that
    // replaced it.
    if (this.isDestroyed) {
      return;
    }

    if (!response.statusFlag || !(response.content?.Items?.length > 0)) {
      return;
    }

    const items = this.processHistoryItems(response.content.Items);
    const newRecords: any[] = [];
    const updatedRecords: any[] = [];

    // Check each item - update existing or add new
    items.forEach((newItem: any) => {
      // Check if exists in cache
      const cacheIndex = this.findRecordIndex(this.allHistoryItems, newItem);

      // Check if exists in pending
      const pendingIndex = this.findRecordIndex(this.pendingNewRecords, newItem);

      if (cacheIndex !== -1) {
        // Update existing record in cache
        this.allHistoryItems[cacheIndex] = newItem;
        updatedRecords.push(newItem);
      } else if (pendingIndex !== -1) {
        // Update existing record in pending
        this.pendingNewRecords[pendingIndex] = newItem;
        updatedRecords.push(newItem);
      } else {
        // New record
        newRecords.push(newItem);
      }
    });

    if (newRecords.length > 0) {
      console.log(`Found ${newRecords.length} new records`);
      this.pendingNewRecords.push(...newRecords);
      this.hasNewRecordsAvailable = true;
    }

    if (updatedRecords.length > 0) {
      console.log(`Updated ${updatedRecords.length} existing records`);
      this.refreshOpenLoanDetail(updatedRecords);
      this.refreshCheckedLoans(updatedRecords);
    }

    // Auto-update if user is on page 1 (index 0) and not filtering
    if (this.currentPageIndex === 0 && (newRecords.length > 0 || updatedRecords.length > 0)) {
      this.applyPendingNewRecords();
    }
  }

  /**
   * Pushes a refreshed copy of the open loan to the file detail view.
   *
   * The poll replaces cached records with new objects, so the detail view keeps
   * rendering the object captured when the loan was clicked. Without this, the
   * per-file messages a user is watching stay frozen at whatever they were when
   * they opened the loan, even though the table behind it is up to date.
   *
   * @param updatedRecords - Records the poll found changes for.
   */
  private refreshOpenLoanDetail(updatedRecords: any[]): void {
    // `onHistoryLoanFieldSelected` is app-wide, so this is the one place a destroyed
    // instance could still redirect the detail view of a live one: it publishes the
    // loan *this* instance had open, which is whatever the user was looking at before
    // they navigated away. takeUntil does not cover it, since the component is the
    // producer here rather than a subscriber.
    if (this.isDestroyed) {
      return;
    }

    const openIdentity = this.recordIdentity(this.selectedLoan);
    if (openIdentity === undefined) {
      return;
    }

    const refreshed = updatedRecords.find(
      (record: any) => this.recordIdentity(record) === openIdentity
    );
    if (!refreshed) {
      return;
    }

    this.selectedLoan = refreshed;
    this.selectedFieldService.onHistoryLoanFieldSelected.next(refreshed);
  }

  /**
   * Angular lifecycle hook: called after view and child views are initialized.
   *
   * - Sets default page size on the paginator.
   * - Connects MatSort to dataSource.
   * - Performs initial data load.
   * - Subscribes to paginator `page` events for dynamic loading.
   */
  ngAfterViewInit(): void {
    if (this.paginator) {
      this.paginator.pageSize = this.currentPageSize;
    }

    // Connect MatSort to dataSource for sorting functionality
    if (this.sort) {
      this.dataSource.sort = this.sort;
      
      // Set default sort to date (descending - newest first)
      this.sort.active = 'date';
      this.sort.direction = 'desc';
      
      // Listen to sort changes
      this.sort.sortChange.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        console.log('Sort changed:', this.sort.active, this.sort.direction);
        
        // Use a timeout to show spinner only if sort takes > 300ms
        let spinnerTimeout: any;
        let sortCompleted = false;
        
        // Set a timeout to show spinner after 300ms if sort hasn't completed
        spinnerTimeout = setTimeout(() => {
          if (!sortCompleted) {
            console.log('[DEBUG] Sort taking longer than 300ms, showing spinner');
            this.isSortingData = true;
            this.cd.detectChanges();
          }
        }, 300);
        
        // Use setTimeout to allow UI to update before sorting
        setTimeout(() => {
          const startTime = Date.now();
          
          // When sort changes, apply it to the current data
          this.applySortToData();
          
          const sortDuration = Date.now() - startTime;
          console.log(`[DEBUG] Sorting took ${sortDuration}ms`);
          
          // Mark sort as completed
          sortCompleted = true;
          
          // Clear the spinner timeout if sort completed quickly
          clearTimeout(spinnerTimeout);
          
          // Hide spinner if it was shown
          if (this.isSortingData) {
            console.log('[DEBUG] Hiding sort spinner');
            this.isSortingData = false;
            this.cd.detectChanges();
          }
        }, 10);
      });
    }

    // Initial load
    this.getHistory(0, this.currentPageSize, true); 

    this.paginator.page.pipe(takeUntil(this.unsubscribe),debounceTime(150)).subscribe(event => {
      // Only call getHistory if NOT filtering (when filtering, MatPaginator handles it automatically)
      const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;
      
      if (!isFiltering) {
        // Clear the "more records available" message when user clicks any navigation button
        // (except when clicking Last Page button to fetch more)
        const lastPageIndex = this.paginator.getNumberOfPages() - 1;
        const previousPageIndex = event.previousPageIndex ?? 0;
        const currentPageIndex = event.pageIndex;
        const hasMoreData = this.lastEvaluatedKey !== null && !this.isEndOfResults;
        
        // User clicked "Last Page" button if:
        // 1. Going to the last page AND
        // 2. Not coming from the second-to-last page (not just clicking "Next")
        const clickedLastPageButton = 
          currentPageIndex === lastPageIndex && 
          previousPageIndex < lastPageIndex - 1 &&
          hasMoreData;
        
        if (clickedLastPageButton) {
          // User clicked "Last Page" button - fetch all remaining records
          console.log('Last Page button clicked - fetching all remaining records');
          this.fetchToEnd();
        } else {
          // Clear the message when navigating (any button except Last Page to fetch more)
          if (this.showMoreRecordsAvailable) {
            console.log('Navigation detected, clearing showMoreRecordsAvailable flag');
            this.showMoreRecordsAvailable = false;
          }
          
          // Normal pagination - just fetch the next page if needed
          const reset = this.dataSource.filter !== this.lastFilter;
          this.getHistory(event.pageIndex, event.pageSize, reset);
        }
      }
    });
  }

  private async loadSettings() {
    this.settings = await this.retrieveSettings();
  }

  /**
   * Sets up debounced filter search to handle API calls for missing records
   */
  private setupFilterDebounce(): void {
    this.filterSubject.pipe(
      debounceTime(600), // Wait 600ms after user stops typing
      distinctUntilChanged(), // Only emit if the value has changed
      takeUntil(this.unsubscribe)
    ).subscribe(async (filterValue: string) => {
      // If filter is cleared, refresh the data
      if (!filterValue || filterValue.length === 0) {
        await this.handleFilterClear();
      } else if (filterValue.length >= 2 && !this.isSearching) {
        // If all records are already loaded (isEndOfResults = true), just filter locally
        if (this.isEndOfResults && this.allHistoryItems.length > 0) {
          console.log('All records loaded, filtering locally without API call');
          this.filterLocally(filterValue);
        } else {
          // Otherwise, search via API for missing records
          await this.searchAllMatchingRecords(filterValue);
        }
      }
    });
  }

  private async validateNXTSoftUser() {
    if (await this.amplifyService.checkNXTsoftUserGroup()) {
      this.isNXTSoftUser = true;
    }
  }
  
  /**
   * Total number of documents in a run, loan documents plus trailing documents.
   *
   * Must include trailing documents because transmittedFileCount does. Counting
   * only `files` here produced totals smaller than the transmitted count, which
   * rendered as nonsense like "12 of 8 files transmitted".
   *
   * @param element - A history record.
   * @returns Count of all documents associated with the run.
   */
  totalFileCount(element: any): number {
    const files = element?.files ?? [];
    const trailing = element?.trailingDocs;

    const trailingTotal = trailing
      ? Object.keys(trailing.inProcess ?? {}).length
        + Object.keys(trailing.processed ?? {}).length
        + Object.keys(trailing.failed ?? {}).length
      : 0;

    return files.length + trailingTotal;
  }

  /**
   * Number of trailing documents that failed.
   *
   * Includes documents stalled past the transfer window. See trailing-docs.util
   * for the rules.
   *
   * @param element - A history record.
   * @returns Count of failed trailing documents.
   */
  failedTrailingDocCount(element: any): number {
    return trailingDocsUtil.countFailed(element?.trailingDocs, element?.date);
  }

  /**
   * Number of trailing documents currently mid-flight.
   *
   * @param element - A history record.
   * @returns Count of trailing documents in the inProcess state.
   */
  trailingDocsInProcessCount(element: any): number {
    const inProcess = element?.trailingDocs?.inProcess;
    if (!inProcess) {
      return 0;
    }
    return Object.keys(inProcess).length;
  }

  /**
   * Which phase an active trailing documents run is in, or null when none is.
   *
   * Mirrors how the loan document pipeline distinguishes Retrieving from
   * Transferring, using the same file messages, so a slow trailing run points at
   * the right service. Presence in `inProcess` is not enough on its own: entries
   * that have finished can sit there carrying "Loan file exported successfully",
   * and treating those as active made the row claim to be working when it was not.
   *
   * Gated on record recency for the same reason the Queued state is. A trailing
   * run that dies mid-flight leaves its entries parked in inProcess, and without
   * the age check the row would advertise itself as running indefinitely.
   *
   * @param element - A history record.
   * @returns TRANSFERRING, RETRIEVING, or null when no trailing run is in flight.
   */
  trailingDocsPhase(element: any): string | null {
    // Shares its basis with the pending tally, so a row cannot report progress
    // while the same documents are counted as failed.
    const active = trailingDocsUtil.activeInProcess(element?.trailingDocs, element?.date);
    if (active.length === 0) {
      return null;
    }

    // A document handed to the transfer service outranks one still being prepared,
    // so the row reports the furthest point the run has reached.
    return active.some(trailingDocsUtil.hasReachedTransfer)
      ? STATUS_VALUES.TRANSFERRING
      : STATUS_VALUES.RETRIEVING;
  }



  /**
   * Whether every loan document on the record transferred successfully.
   *
   * Deliberately ignores trailing documents so the two can be judged separately.
   *
   * @param element - A history record.
   * @returns True when at least one loan document exists and all were exported.
   */
  private allLoanFilesExported(element: any): boolean {
    const files = element?.files ?? [];
    if (files.length === 0) {
      return false;
    }
    return files.every((f: any) =>
      (f?.statusMessage?.toLowerCase() ?? '').includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED)
    );
  }

  /**
   * Whether the only thing that went wrong on this run was trailing documents.
   *
   * This is the partial success case: the loan documents reached the core, so
   * resending them would duplicate work, but the trailing documents still need
   * attention and have no retry path.
   *
   * @param element - A history record.
   * @returns True when loan documents all succeeded and trailing documents failed.
   */
  private onlyTrailingDocsFailed(element: any): boolean {
    return this.allLoanFilesExported(element) && this.failedTrailingDocCount(element) > 0;
  }

  /**
   * Milliseconds since the record was last written by the backend.
   *
   * The backend refreshes `date` on every status update, so this is the time of
   * the most recent activity on the loan rather than when the run started.
   *
   * @param element - A history record.
   * @returns Age in milliseconds, or undefined when the date cannot be read.
   */
  private activityAgeMs(element: any): number | undefined {
    const raw = element?.date;
    if (raw === undefined || raw === null) {
      return undefined;
    }
    const ms = typeof raw === 'string' ? new Date(raw).getTime() : Number(raw);
    if (isNaN(ms)) {
      return undefined;
    }
    return Date.now() - ms;
  }

  /**
   * Whether the loan was acted on moments ago.
   *
   * Lets a second person opening the table see that a colleague just ran this
   * loan, instead of resending it a second time.
   *
   * @param element - A history record.
   * @returns True when the last activity falls inside the just-run window.
   */
  isJustRun(element: any): boolean {
    const age = this.activityAgeMs(element);
    return age !== undefined && age >= 0 && age <= JUST_RUN_WINDOW_MS;
  }

  /**
   * Short human description of when the loan was last acted on.
   *
   * Returns an empty string once the activity is old enough that the absolute
   * date in the same column is the clearer reference.
   *
   * @param element - A history record.
   * @returns Relative description such as "just now" or "12 min ago".
   */
  relativeActivity(element: any): string {
    const age = this.activityAgeMs(element);
    if (age === undefined || age < 0) {
      return '';
    }
    if (age <= JUST_RUN_WINDOW_MS) {
      return 'just now';
    }

    const minutes = Math.floor(age / 60000);
    if (minutes < 60) {
      return `${minutes} min ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hr ago`;
    }

    return '';
  }

  /**
   * Tooltip for the date cell, clarifying that the timestamp tracks the most
   * recent activity rather than when the run began.
   *
   * @param element - A history record.
   * @returns Tooltip text.
   */
  activityTooltip(element: any): string {
    const relative = this.relativeActivity(element);
    if (this.isInProgress(element)) {
      return relative
        ? `Currently running. Last update ${relative}.`
        : 'Currently running.';
    }
    return relative ? `Last updated ${relative}.` : '';
  }

  /**
   * Whether a loan is still working, so the table can show live progress.
   *
   * Covers both active states: waiting on Encompass, and actively transferring.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is still in flight.
   */
  isInProgress(element: any): boolean {
    const status = this.basePhase(this.getStatus(element));
    return status === STATUS_VALUES.QUEUED
      || status === STATUS_VALUES.RETRIEVING
      || status === STATUS_VALUES.TRANSFERRING;
  }

  /**
   * Whether documents are still coming back from Encompass.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is retrieving.
   */
  isRetrieving(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.RETRIEVING;
  }

  /**
   * Whether the run has been accepted but has not reported yet.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is queued.
   */
  isQueued(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.QUEUED;
  }

  /**
   * Whether a run has sat waiting long enough, with nothing returned from Encompass,
   * that document retrieval can be called failed.
   *
   * Requires both conditions. The age alone is not enough, because a slow run that has
   * files coming back is still working. No documents alone is not enough either, since
   * a freshly accepted run legitimately has none yet.
   *
   * @param element - A history record.
   * @returns True when the run never retrieved anything and has waited past the limit.
   */
  hasStalledBeforeRetrieving(element: any): boolean {
    if (this.totalFileCount(element) > 0) {
      return false;
    }

    const age = this.activityAgeMs(element);
    return age !== undefined && age > this.QUEUED_RETRIEVAL_TIMEOUT_MS;
  }

  /**
   * Whether the transfer service has taken the batch and is sending files.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is transferring.
   */
  isTransferring(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.TRANSFERRING;
  }

  /**
   * Whether a loan finished successfully.
   *
   * @param element - A history record.
   * @returns True when every file was confirmed exported.
   */
  isSuccessful(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.SUCCESSFUL;
  }

  /**
   * Whether a loan finished without transferring everything.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is a failure.
   */
  isUnsuccessful(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.UNSUCCESSFUL;
  }

  /**
   * Whether the loan documents transferred but trailing documents did not.
   *
   * @param element - A history record.
   * @returns True when the record's derived status is a partial success.
   */
  isPartialSuccess(element: any): boolean {
    return this.basePhase(this.getStatus(element)) === STATUS_VALUES.PARTIAL_SUCCESS;
  }

  /**
   * Builds the tooltip for the status cell.
   *
   * Prefers the detailed backend status when the user is permitted to see it,
   * and otherwise explains what the displayed state means. In-progress rows get
   * a file tally so it is clear work is still outstanding rather than stalled.
   *
   * @param element - A history record.
   * @returns Tooltip text, or an empty string when there is nothing to add.
   */
  /**
   * Builds the tooltip for a row whose status describes a trailing documents run.
   *
   * @param element - A history record.
   * @returns Tooltip text, or an empty string when no trailing run is active.
   */
  trailingDocsTooltip(element: any): string {
    const phase = this.trailingDocsPhase(element);
    if (!phase) {
      return '';
    }

    const inFlight = this.trailingDocsInProcessCount(element);
    let tooltip = phase === STATUS_VALUES.TRANSFERRING
      ? `Sending trailing documents to the destination. ${inFlight} in progress.`
      : `Retrieving trailing documents from Encompass. ${inFlight} in progress.`;

    const failed = this.failedTrailingDocCount(element);
    if (failed > 0) {
      tooltip += ` ${failed} earlier trailing document(s) failed and need manual follow-up.`;
    }

    // The retry control only resends loan documents, so say so before someone
    // reaches for it on a row that is visibly working on something else.
    return tooltip + ' Trailing documents cannot be retried from this screen.';
  }

  statusTooltip(element: any): string {
    const detailed = this.getDetailedStatus(element);
    if (detailed) {
      return detailed;
    }

    // Checked before the generic in-progress branch, which would otherwise
    // describe the loan document phase for a status that belongs to the trailing
    // run and quote a file tally covering both.
    if (this.isReportingTrailingPhase(element)) {
      return this.trailingDocsTooltip(element);
    }

    if (this.isInProgress(element)) {
      const total = this.totalFileCount(element);
      const done = this.transmittedFileCount(element);
      // Only quote a ratio when it makes sense. If the two counts ever disagree
      // again, report the plain count rather than an impossible fraction.
      const canShowRatio = total > 0 && done <= total;

      let progress: string;
      if (this.isTransferring(element)) {
        if (canShowRatio) {
          progress = `Sending to the destination. ${done} of ${total} files transmitted.`;
        } else if (done > 0) {
          progress = `Sending to the destination. ${done} files transmitted.`;
        } else {
          progress = 'Sending to the destination.';
        }
      } else if (this.isRetrieving(element)) {
        // Report only the documents still pending, not every document seen so far.
        // totalFileCount includes everything already processed/transmitted, which
        // made a run that had mostly finished retrieving still claim a large count.
        const pending = this.pendingFileCount(element);
        progress = pending > 0
          ? `Retrieving documents from Encompass. ${pending} pending.`
          : 'Retrieving documents from Encompass.';
      } else {
        progress = 'Accepted for processing. Waiting for the first update.';
      }

      const age = this.activityAgeMs(element);
      if (age !== undefined && age > TIME_THRESHOLD_MS) {
        // Explain why a long-running transfer is still shown as in progress.
        return `${progress} Taking longer than usual, but still making progress.`;
      }
      return progress;
    }

    if (this.isPartialSuccess(element)) {
      const trailing = this.failedTrailingDocCount(element);
      return `Loan documents transferred. ${trailing} trailing document(s) failed `
        + 'and cannot be retried from this screen, so they need manual follow-up.';
    }

    if (this.isUnsuccessful(element)) {
      // A run that never retrieved anything and has now been resolved as failed.
      // Name the retrieval failure rather than pointing at files that do not exist.
      if (this.hasStalledBeforeRetrieving(element)) {
        return 'Failed to retrieve files from Encompass. No documents came back, '
          + 'so nothing could be transferred. This run needs to be retried.';
      }
      const failed = this.failedFileCount(element);
      if (failed > 0) {
        return `${failed} file(s) did not transfer. Open the loan for details.`;
      }
      if (this.hasErrorDetail(element)) {
        // No individual file is marked failed, so without this the tooltip would
        // suggest looking at files that all report success. The run itself failed.
        return 'This run reported an error before completing. The file statuses '
          + 'below are from an earlier attempt. Open the loan for details.';
      }
      return 'This run did not complete. Open the loan for details.';
    }

    return '';
  }

  /**
   * Reads the detailedStatus message off a record, ignoring user permissions.
   *
   * Deliberately separate from getDetailedStatus, which returns an empty string
   * for non-NXTSoft users. Status derivation must not depend on who is looking:
   * gating this would show internal users Unsuccessful and clients Successful for
   * the same record.
   *
   * @param item - A history record.
   * @returns The detail message, or an empty string when there is none.
   */
  private rawDetailMessage(item: any): string {
    const detail = item?.detailedStatus;
    const entry = Array.isArray(detail) ? detail[0] : detail;
    if (!entry) {
      return '';
    }

    // Combine every text field rather than picking one. The backend splits the
    // reason across these: a generic `message` ("Failed to retrieve loan
    // information token") alongside a specific `response.statusMessage` ("Expired
    // Token"). Reading only one missed the error when the failure wording lived in
    // the other, so a real failure read as benign.
    return [
      entry?.message,
      entry?.response?.details,
      entry?.response?.statusMessage
    ]
      .filter((part) => part !== undefined && part !== null && part !== '')
      .map((part) => String(part))
      .join(' ');
  }

  /**
   * Parses a value that may be a millisecond number or a date string into ms.
   *
   * @param value - A stored date, either epoch ms or an ISO string.
   * @returns The time in ms, or undefined when it cannot be read.
   */
  private toMs(value: number | string | undefined | null): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }
    const ms = typeof value === 'string' ? new Date(value).getTime() : Number(value);
    return isNaN(ms) ? undefined : ms;
  }

  /**
   * Whether the detailedStatus describes the current run rather than a leftover
   * from an earlier attempt.
   *
   * The backend stamps each detailedStatus with the time it was written, and stamps
   * each file with the time it was retrieved. A detailedStatus written at or after
   * the newest file means the run failed after retrieving, so the message is current.
   * One written before the newest file predates this run's retrieval and is stale.
   *
   * Both dates are optional. Older records were written before either field existed,
   * so when a date is missing this returns true and the caller falls back to its
   * prior behaviour rather than suppressing a real error.
   *
   * @param element - A history record.
   * @returns True when the detail should be trusted as current.
   */
  private detailIsCurrent(element: any): boolean {
    const detail = element?.detailedStatus;
    const entry = Array.isArray(detail) ? detail[0] : detail;
    const detailMs = this.toMs(entry?.date);
    if (detailMs === undefined) {
      return true;
    }

    const files = element?.files ?? [];
    const fileMs = files
      .map((file: any) => this.toMs(file?.date))
      .filter((ms: number | undefined): ms is number => ms !== undefined);
    if (fileMs.length === 0) {
      return true;
    }

    return detailMs >= Math.max(...fileMs);
  }

  /**
   * Whether a dated detailedStatus is fresh enough to fail an otherwise in-progress
   * row.
   *
   * The backend stamps a detailedStatus with a date only when it writes a status,
   * so a present detail date is positive proof of a fresh backend write. That is the
   * hard requirement: without it there is no proof, and an in-progress row must not
   * be failed on a guess.
   *
   * The file dates refine it. When the current run retrieved files, they carry this
   * run's date, so the detail must be newer than the newest of them to prove it
   * post-dates retrieval rather than being a leftover. When no file carries a date -
   * either the run failed before retrieving anything, or the files are legacy leftovers
   * from a prior run written before dating existed - there is no current-run file to
   * contradict the detail, so a dated detail wins on its own.
   *
   * @param element - A history record.
   * @returns True when a dated detail should fail an in-progress row.
   */
  private detailDatedAfterFiles(element: any): boolean {
    const detail = element?.detailedStatus;
    const entry = Array.isArray(detail) ? detail[0] : detail;
    const detailMs = this.toMs(entry?.date);
    if (detailMs === undefined) {
      return false;
    }

    const files = element?.files ?? [];
    const fileMs = files
      .map((file: any) => this.toMs(file?.date))
      .filter((ms: number | undefined): ms is number => ms !== undefined);
    if (fileMs.length === 0) {
      return true;
    }

    return detailMs > Math.max(...fileMs);
  }

  /**
   * Whether the record's detail message reports a failure.
   *
   * @param element - A history record.
   * @returns True when the detail message reads as an error.
   */
  hasErrorDetail(element: any): boolean {
    const message = this.rawDetailMessage(element).toLowerCase();
    if (!message) {
      return false;
    }

    // Informational messages win outright, including the milestone skip notice.
    if (DETAIL_BENIGN_FRAGMENTS.some((fragment) => message.includes(fragment))) {
      return false;
    }

    // A detail left over from an earlier attempt must not fail a run that has since
    // retrieved successfully. Only enforced when the dates are present; absent them
    // this is a no-op and the message is judged on its text alone.
    if (!this.detailIsCurrent(element)) {
      return false;
    }

    return DETAIL_ERROR_FRAGMENTS.some((fragment) => message.includes(fragment));
  }

  getDetailedStatus(item: any): string {
    if (!this.isNXTSoftUser) {
      return "";
    }
    // Check if 'detailedStatus' is an array and has at least one item
    if (Array.isArray(item?.detailedStatus) && item.detailedStatus.length > 0) {
      const firstItem = item.detailedStatus[0];
  
      if (firstItem?.response?.details) {
        return String(firstItem.response.details);
      }
      else if (firstItem?.message) {
        return String(firstItem.message);
      }
    }
    // If 'detailedStatus' is not an array, check if 'response.details' exists
    else if (item?.detailedStatus?.response?.details) {
      return String(item.detailedStatus.response.details);
    }
    else if (item?.detailedStatus?.message) {
      return String(item.detailedStatus.message);
    }
  
    return "";
  }
  
  
  /**
   * Maps the stored `status` field onto a known STATUS_VALUES member.
   *
   * Pure mapping, with none of the derivation getStatus layers on top. Kept
   * separate because processHistoryItems writes its result back to `item.status`,
   * and persisting a derived value there is not safe: a trailing phase written into
   * the record would still be read back after the trailing run finished, leaving
   * the row reporting Transferring permanently.
   *
   * @param element - A history record.
   * @returns The stored status, normalized.
   */
  private normalizeStoredStatus(element: any): string {
    const status = element?.status;
    switch (typeof status) {
      case 'boolean':
        return status ? STATUS_VALUES.SUCCESSFUL : STATUS_VALUES.UNSUCCESSFUL;
      case 'string': {
        const known = [
          STATUS_VALUES.SUCCESSFUL,
          STATUS_VALUES.QUEUED,
          STATUS_VALUES.RETRIEVING,
          STATUS_VALUES.TRANSFERRING,
          STATUS_VALUES.PARTIAL_SUCCESS
        ].find((value) => value.toLowerCase() === status.toLowerCase());
        return known ?? STATUS_VALUES.UNSUCCESSFUL;
      }
      default:
        return STATUS_VALUES.UNSUCCESSFUL;
    }
  }

  getStatus(element:any): string  {
      let currentStatus = this.normalizeStoredStatus(element);
      // failedFileCount reads element.files itself, so it needs the record and
      // not the files array. Passing the array made this guard always see zero
      // failures, which let a run report Successful while files had failed.
      if (currentStatus === STATUS_VALUES.SUCCESSFUL && this.failedFileCount(element) > 0) {
        // Trailing documents failing on their own is a partial success, not an
        // outright failure. Calling it Unsuccessful would point support at the
        // retry button, which only resends loan documents.
        return this.onlyTrailingDocsFailed(element)
          ? STATUS_VALUES.PARTIAL_SUCCESS
          : STATUS_VALUES.UNSUCCESSFUL;
      }

      // An in-flight trailing run reports its own phase, the same way a loan
      // document run does.
      //
      // Skipped when the record is already Unsuccessful, so a failed loan document
      // run is never masked by trailing progress, and skipped when the loan
      // documents are themselves in flight, since their status already describes an
      // active run and the trailing phase can be behind it.
      //
      // Partial Success may be superseded here: an active run may be reprocessing
      // the trailing documents that failed, and the tooltip keeps reporting them.
      if (currentStatus !== STATUS_VALUES.UNSUCCESSFUL && !this.loanDocsInFlight(element)) {
        const trailingPhase = this.trailingDocsPhase(element);
        if (trailingPhase) {
          // Plain phase, not the qualified label. Every === comparison against
          // STATUS_VALUES depends on this, as does the status column sort.
          return trailingPhase;
        }
      }

      // A reported error in detailedStatus outranks a stale Successful. The backend
      // writes the detail unconditionally but only promotes the record to
      // Unsuccessful when it has no files or a file already reads as failed, so a
      // run that died carrying files from an earlier attempt keeps reporting
      // Successful with the failure visible only in the tooltip.
      //
      // Ordered after the trailing phase because observed progress is fresher
      // evidence than a persisted message, which may describe an earlier attempt.
      if (currentStatus === STATUS_VALUES.SUCCESSFUL && this.hasErrorDetail(element)) {
        return STATUS_VALUES.UNSUCCESSFUL;
      }

      // A run that dies mid-retrieval used to sit at Retrieving until the file-age
      // stall check aged it out, which takes TIME_THRESHOLD_MS. When the backend
      // records an error stamped after the files were retrieved, that is proof the
      // run failed this time around, so the row can fail immediately rather than
      // appearing stuck.
      //
      // Gated on detailDatedAfterFiles rather than hasErrorDetail alone: an
      // in-progress row has no stored failure to fall back on, so promoting it on a
      // possibly-stale detail would kill a genuinely active run. The date check is
      // what makes this safe, and it is a no-op on records without the dates.
      const inProgress = currentStatus === STATUS_VALUES.RETRIEVING
        || currentStatus === STATUS_VALUES.TRANSFERRING
        || currentStatus === STATUS_VALUES.QUEUED;
      if (inProgress && this.detailDatedAfterFiles(element) && this.hasErrorDetail(element)) {
        return STATUS_VALUES.UNSUCCESSFUL;
      }

      // A run that stops moving must not sit at Queued forever. Any in-flight trailing
      // work has already returned its phase above, so reaching here still in progress
      // means nothing is active: either Queued with nothing retrieved, or a run whose
      // last activity has aged past the stall window. Resolve it to a terminal state
      // so the row does not report progress indefinitely.
      //
      // Partial Success when the loan documents did make it (only trailing work is
      // outstanding, which cannot be retried from here), otherwise Unsuccessful.
      if (inProgress && this.hasStalled(element)) {
        return this.allLoanFilesExported(element)
          ? STATUS_VALUES.PARTIAL_SUCCESS
          : STATUS_VALUES.UNSUCCESSFUL;
      }

      return currentStatus;
  }

  /**
   * Whether an in-progress run has stopped advancing and should be treated as done.
   *
   * Two ways a run stalls: it sat at Queued past the retrieval window without a
   * single document coming back, or its most recent activity has aged past the
   * transfer window. Both are measured from the record date, which the backend
   * refreshes on every write, so a run that is genuinely still moving keeps resetting
   * the clock and never trips this.
   *
   * Requires a readable date. Without one there is no evidence the run is stuck, and
   * a run must not be failed on a guess.
   *
   * @param element - A history record.
   * @returns True when the run has stalled and should resolve to a terminal state.
   */
  private hasStalled(element: any): boolean {
    if (this.hasStalledBeforeRetrieving(element)) {
      return true;
    }

    // Nothing retrieved yet is governed solely by the retrieval timeout checked
    // above. Without this, the 10-minute activity check below fired first and failed
    // a Queued run at 10 minutes instead of its documented 15-minute window.
    if (this.totalFileCount(element) === 0) {
      return false;
    }

    // Still visibly moving and under the in-progress ceiling. This mirrors the grace
    // window processHistoryItems uses to keep a slow transfer in progress; without it
    // getStatus re-derived the same record as failed and the grace path never showed.
    if (this.isRecentlyActive(element) && !this.hasExceededMaxInProgress(element)) {
      return false;
    }

    const age = this.activityAgeMs(element);
    return age !== undefined && age > TIME_THRESHOLD_MS;
  }

  /**
   * The status as displayed, qualified when it describes a trailing documents run.
   *
   * Kept separate from getStatus so the qualified form never reaches a `===`
   * comparison against STATUS_VALUES, and never reaches `item.status`, which the
   * status column sort reads directly.
   *
   * @param element - A history record.
   * @returns `Retrieving - Trailing Docs`, `Transferring - Trailing Docs`, or the
   *          plain status.
   */
  getStatusLabel(element: any): string {
    const status = this.getStatus(element);

    if (this.isReportingTrailingPhase(element)) {
      return `${status}${TRAILING_DOCS_STATUS_SUFFIX}`;
    }

    return status;
  }

  /**
   * Whether the displayed status describes the trailing run rather than the loan
   * documents.
   *
   * A record can have trailing documents in flight while its status belongs to the
   * loan documents, either because they failed or because they are still moving
   * themselves. Attributing the phase to trailing documents in those cases would
   * mislabel the row, so both the label and the tooltip route through this.
   *
   * @param element - A history record.
   * @returns True when the status is the trailing run's own phase.
   */
  private isReportingTrailingPhase(element: any): boolean {
    const phase = this.trailingDocsPhase(element);
    return phase !== null
      && !this.loanDocsInFlight(element)
      && this.getStatus(element) === phase;
  }

  /**
   * Whether any loan document is still being prepared or sent.
   *
   * Mirrors the in-flight test used when deriving the run status, so the two agree
   * on whether the loan documents still have work outstanding.
   *
   * @param element - A history record.
   * @returns True when at least one loan document is in flight.
   */
  private loanDocsInFlight(element: any): boolean {
    const files = element?.files ?? [];
    return files.some((file: any) => {
      const statusMessage = file?.statusMessage?.toLowerCase() ?? '';
      return statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED)
        // Matches the shorter fragment the status pipeline uses, rather than the
        // full STATUS_MESSAGES.PROCESSED_FILE value, so the two agree. A false
        // negative here would wrongly attribute the phase to trailing documents.
        || statusMessage.includes('processed file')
        || statusMessage.includes(STATUS_MESSAGES.SENDING_FILE);
    });
  }

  /**
   * Strips the trailing documents qualifier from a status value.
   *
   * Defensive. `getStatus` returns plain values and only `getStatusLabel` applies
   * the qualifier, but the `is*` helpers compare with `===`, so a qualified value
   * reaching them would silently disable the spinner and the row styling.
   *
   * @param status - A status value, qualified or plain.
   * @returns The plain status value.
   */
  private basePhase(status: unknown): string {
    if (typeof status !== 'string') {
      return '';
    }
    return status.endsWith(TRAILING_DOCS_STATUS_SUFFIX)
      ? status.slice(0, -TRAILING_DOCS_STATUS_SUFFIX.length)
      : status;
  }

  /**
   * Fetches document connector history with smart pagination and caching.
   * 
   * @param pageIndex - The zero-based index of the page to display (default: 0).
   * @param pageSize - Number of items per page (default: 50). Controlled by MatPaginator.
   * @param reset - If true, clears all cached data and starts fresh (used for refresh/filter).
   */
  async getHistory(pageIndex: number = 0, pageSize: number = 50, reset = false) {
    if (this.isFetchingHistory) return;
    
    if (pageIndex === 0 && this.hasNewRecordsAvailable && !reset) {
      this.applyPendingNewRecords();
    }
    
    const requiredCount = (pageIndex + 1) * pageSize;
    const hasEnoughData = this.allHistoryItems.length >= requiredCount;
    
    // This prevents unnecessary fetches when navigating between already-loaded pages
    if (hasEnoughData && !reset) {
      console.log(`Already have data for page ${pageIndex}, just updating display`);
      this.currentPageIndex = pageIndex;
      this.currentPageSize = pageSize;
      
      // Update displayed data
      const start = pageIndex * pageSize;
      const end = start + pageSize;
      this.dataSource.data = this.allHistoryItems.slice(start, end);
      
      // Update paginator using centralized method
      this.updatePaginator();
      
      return;
    }
    
    this.isFetchingHistory = true;
    this.spinnerService.setShowSpinner(true);

    // Reset cache on full refresh (refresh button) or filter change
    if (reset || this.dataSource.filter !== this.lastFilter) {
      this.lastEvaluatedKey = undefined;
      this.currentPageIndex = 0;
      this.isEndOfResults = false;
      this.totalLoaded = 0;
      this.allHistoryItems = [];
      this.clearProgressTracking();
    }

    if (pageSize !== this.currentPageSize) {
      this.currentPageIndex = 0;  
    }

    this.currentPageIndex = pageIndex;
    this.currentPageSize = pageSize;

    try {
      // Check if we're in filter mode
      const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;
      
      // Only fetch more data from DB if NOT filtering
      if (!isFiltering) {
        // Fetch more only if we don't have enough items
        const requiredCount = (pageIndex + 1) * pageSize;
        while (this.allHistoryItems.length < requiredCount && !this.isEndOfResults) {
          const response = await this.dcService.getMoreDocConnectorHistory(
            this.assetService.getSelectedAssetId(),
            this.lastEvaluatedKey
          ) as DocConnectorHistoryResponse;

          if (!response.statusFlag) {
            this.handleError(response.statusMessage ?? 'Unknown error');
            // Don't set isEndOfResults on error - just break
            break;
          }

          // Check if we have items in the response
          const responseItems = response.content?.Items || [];
          
          if (responseItems.length === 0 && !response.content?.LastEvaluatedKey) {
            // No items and no more pages - truly end of results
            this.isEndOfResults = true;
            break;
          }

          const items = this.processHistoryItems(responseItems);
          
          // Remove old instances (from search) and add new ones (from pagination)
          items.forEach(newItem => {
            const existingIndex = this.findRecordIndex(this.allHistoryItems, newItem);

            if (existingIndex !== -1) {
              // Duplicate found - remove the old instance (likely from search)
              console.log(`Removing duplicate item from search results: ${newItem.loanNumber || newItem.loanGUID}`);
              this.allHistoryItems.splice(existingIndex, 1);
              this.totalLoaded--; // Adjust count since we're replacing, not adding
            }
          });
          
          // Add all new items (duplicates have been removed above)
          this.allHistoryItems.push(...items);
          this.totalLoaded += items.length;

          // Update pagination token
          this.lastEvaluatedKey = response.content.LastEvaluatedKey || null;
          
          // Only set isEndOfResults if there's no more data to fetch
          if (!this.lastEvaluatedKey) {
            this.isEndOfResults = true;
            break;
          }
        }
      }

      // Apply current sort to ensure items are in correct order
      if (this.sort && this.sort.active) {
        this.applySortToData();
      } else {
        // Default sort by date if no sort is active
        this.allHistoryItems.sort((a, b) => {
          const dateA = typeof a.date === 'string' ? new Date(a.date).getTime() : a.date;
          const dateB = typeof b.date === 'string' ? new Date(b.date).getTime() : b.date;
          return dateB - dateA; 
        });
      }

      // When filtering, show all items and let MatTableDataSource + MatPaginator handle pagination
      // When not filtering, manually slice the page to display
      if (isFiltering) {
        this.dataSource.data = [...this.allHistoryItems];
        // Connect paginator to data source for automatic pagination
        this.dataSource.paginator = this.paginator;
      } else {
        // Disconnect paginator for manual pagination
        this.dataSource.paginator = null;
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        this.dataSource.data = this.allHistoryItems.slice(start, end);
      }

      this.updatePaginator();
    } catch (e) {
      console.error(e);
    } finally {
      // Ensure UI updates even if async
      setTimeout(() => {
        this.isFetchingHistory = false;
        this.spinnerService.setShowSpinner(false);
        this.lastFilter = this.dataSource.filter ?? '';
      }, 0);
    }
  }

  /**
   * Displays an error dialog with the given message.
   *
   * @param message - The error message to show in the dialog.
   */
  private handleError(message: string) {
    this.dialog.open(ErrorDialogComponent, { data: { title: 'History', message } });
  }

  /**
   * Synchronizes the MatPaginator with current pagination state.
   * This is the single source of truth for paginator updates.
 */
  private updatePaginator() {
    if (this.paginator) {
        // If we've reached the end OR if more records are available (batch limit), show +1 for "load more"
        // Only show exact count if truly at the end with no more data
        const hasMoreData = !this.isEndOfResults || this.showMoreRecordsAvailable;
        
        if (hasMoreData) {
          // Calculate how many records we need to show to create an extra page
          // If we're on the last page, we need to add enough records to create a new page
          const currentPages = Math.ceil(this.totalLoaded / this.currentPageSize);
          const recordsNeededForExtraPage = (currentPages * this.currentPageSize) + 1;
          this.paginator.length = Math.max(this.totalLoaded + 1, recordsNeededForExtraPage);
        } else {
          this.paginator.length = this.totalLoaded;
        }

      // Ensure pageSize stays in sync
      this.paginator.pageSize = this.currentPageSize;
      this.paginator.pageIndex = this.currentPageIndex;
      
      console.log(`Paginator updated: length=${this.paginator.length}, pageIndex=${this.paginator.pageIndex}, totalLoaded=${this.totalLoaded}, isEndOfResults=${this.isEndOfResults}, showMoreRecordsAvailable=${this.showMoreRecordsAvailable}`);
    }
  }

  /**
   * Last known progress fingerprint per record, used to spot movement between polls.
   */
  private progressFingerprints = new Map<string, string>();

  /**
   * When each record was last seen to change.
   */
  private lastObservedChangeAt = new Map<string, number>();

  /**
   * When each record was first seen transferring, so the Queued state can be capped.
   */
  private firstSeenInProgressAt = new Map<string, number>();

  /**
   * Summarises the parts of a record that move while a transfer runs.
   *
   * Compared between polls to tell an actively progressing run apart from one
   * that has stopped. `date` is included because the backend refreshes it on
   * every status write, and the per-file messages are included because they
   * change as individual files complete.
   *
   * @param item - A history record.
   * @returns A comparable summary of the record's progress.
   */
  private progressFingerprint(item: any): string {
    const files = item?.files ?? [];
    const fileStates = files.map((f: any) => f?.statusMessage ?? '').join('|');
    const trailing = item?.trailingDocs;
    const trailingCounts = trailing
      ? [
          Object.keys(trailing.inProcess ?? {}).length,
          Object.keys(trailing.processed ?? {}).length,
          Object.keys(trailing.failed ?? {}).length
        ].join(',')
      : '';
    return `${item?.date ?? ''}~${files.length}~${fileStates}~${trailingCounts}`;
  }

  /**
   * Records whether a record changed since the previous observation.
   *
   * @param item - A freshly fetched history record.
   */
  private trackProgress(item: any): void {
    const identity = this.recordIdentity(item);
    if (identity === undefined) {
      return;
    }

    const fingerprint = this.progressFingerprint(item);
    const previous = this.progressFingerprints.get(identity);

    if (previous !== undefined && previous !== fingerprint) {
      this.lastObservedChangeAt.set(identity, Date.now());
    }
    this.progressFingerprints.set(identity, fingerprint);
  }

  /**
   * Whether a record changed recently enough to still count as progressing.
   *
   * @param item - A history record.
   * @returns True when movement was observed inside the grace window.
   */
  private isRecentlyActive(item: any): boolean {
    const identity = this.recordIdentity(item);
    if (identity === undefined) {
      return false;
    }
    const changedAt = this.lastObservedChangeAt.get(identity);
    return changedAt !== undefined && (Date.now() - changedAt) <= ACTIVITY_GRACE_MS;
  }

  /**
   * Whether a record has been transferring long enough to stop waiting on it.
   *
   * @param item - A history record.
   * @returns True when the record passed the absolute in-progress ceiling.
   */
  private hasExceededMaxInProgress(item: any): boolean {
    const identity = this.recordIdentity(item);
    if (identity === undefined) {
      return false;
    }
    const startedAt = this.firstSeenInProgressAt.get(identity);
    return startedAt !== undefined && (Date.now() - startedAt) > MAX_IN_PROGRESS_MS;
  }

  /**
   * Notes the first time a record was seen transferring, which anchors the
   * ceiling on how long it may report Queued.
   *
   * @param item - A history record currently mid-transfer.
   */
  private noteInProgress(item: any): void {
    const identity = this.recordIdentity(item);
    if (identity === undefined || this.firstSeenInProgressAt.has(identity)) {
      return;
    }
    this.firstSeenInProgressAt.set(identity, Date.now());
  }

  /**
   * Clears per-record progress tracking. Used when the cache is rebuilt so the
   * maps do not grow without bound across asset switches and refreshes.
   */
  private clearProgressTracking(): void {
    this.progressFingerprints.clear();
    this.lastObservedChangeAt.clear();
    this.firstSeenInProgressAt.clear();
    // Deliberately keeps submittedLoans: a manual refresh must not make a
    // just-submitted loan fall back to reporting its previous run's outcome.
  }

  /**
   * Returns the stable identity of a history record.
   *
   * `loanGUID` is the partition key of `encompass_document_connector_history`,
   * so it identifies a record for its whole lifetime. `date` must not be part of
   * the identity: the backend reassigns it on every status update
   * (`updateDocConnectorHistoryStatusMessage`), so keying on it makes an updated
   * record look like a brand new one and produces duplicate rows in the table.
   *
   * @param item - A history record.
   * @returns The record identity, or undefined when it cannot be determined.
   */
  private recordIdentity(item: any): string | undefined {
    return item?.loanGUID ?? undefined;
  }

  /**
   * Identifies table rows for change detection.
   *
   * Without this the table rebuilds every row whenever the data source is
   * reassigned, which happens on each background refresh. Rebuilding destroys the
   * element under the cursor, so an open tooltip closes and the row loses hover.
   * Keying on the loan lets the existing rows be reused and only their contents
   * updated.
   *
   * @param _index - Row index, unused.
   * @param row - The row's record.
   * @returns A stable identity for the row.
   */
  trackByLoan = (_index: number, row: any): any => {
    return this.recordIdentity(row) ?? row;
  };

  /**
   * Whether a record is currently selected for sending.
   *
   * `checkedLoans` is the source of truth for selection, so the checkbox state on
   * a record is derived from it rather than stored independently.
   *
   * @param item - A history record.
   * @returns True when the record is in the current selection.
   */
  private isLoanChecked(item: any): boolean {
    const identity = this.recordIdentity(item);
    if (identity === undefined || !Array.isArray(this.checkedLoans)) {
      return false;
    }
    return this.checkedLoans.some(
      (checked: any) => this.recordIdentity(checked) === identity
    );
  }

  /**
   * Replaces selected records with their refreshed copies.
   *
   * Selection holds record objects, and the background fetch swaps those objects
   * out. Leaving the stale copies in place risks sending an outdated record,
   * which matters because the send path writes the whole record back to DynamoDB.
   *
   * @param updatedRecords - Records the poll found changes for.
   */
  private refreshCheckedLoans(updatedRecords: any[]): void {
    if (!Array.isArray(this.checkedLoans) || this.checkedLoans.length === 0) {
      return;
    }

    let changed = false;
    this.checkedLoans = this.checkedLoans.map((checked: any) => {
      const identity = this.recordIdentity(checked);
      const refreshed = updatedRecords.find(
        (record: any) => this.recordIdentity(record) === identity
      );
      if (refreshed) {
        changed = true;
        return refreshed;
      }
      return checked;
    });

    if (changed) {
      this.selectedFieldService.checkedHistoryLoans.next(this.checkedLoans);
    }
  }

  /**
   * Finds the cached index of a record by its stable identity.
   *
   * @param collection - Cache to search.
   * @param item - Record whose match is being located.
   * @returns Index of the matching record, or -1 when absent.
   */
  private findRecordIndex(collection: any[], item: any): number {
    const identity = this.recordIdentity(item);
    if (identity === undefined) {
      return -1;
    }
    return collection.findIndex(existing => this.recordIdentity(existing) === identity);
  }

  /**
   * Processes raw history items from DynamoDB and enhances them with UI state.
   *
   * @param items - Array of raw history items from the API.
   * @returns Array of processed items with `checkToSend` and derived `status`.
   */
  private processHistoryItems(items: any[]): any[] {
    return items.map(item => {
      // The checkbox is bound to this flag, and the background fetch replaces
      // cached records with fresh objects. Clearing it unconditionally is what
      // made a selection disappear whenever the table refreshed, so it is derived
      // from the current selection instead.
      item.checkToSend = this.isLoanChecked(item);

      // Compare against the previous observation before deriving status, so a
      // slow but progressing transfer can be told apart from a stalled one.
      this.trackProgress(item);

      const files = item.files ?? [];
      // Keep every file in the tally. Dropping files without a statusMessage
      // would let a loan with unreported files read as fully successful.
      const filesStatus = files.map((f: any) => f.statusMessage?.toLowerCase() ?? '');

      const isExported = (s: string) => s.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED);
      const isFailure = (s: string) => TRANSFER_FAILURE_FRAGMENTS.some((fragment) => s.includes(fragment));
      // Retrieved from Encompass or being prepared, but not handed over yet.
      const isPreparing = (s: string) =>
        s.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
        s.includes('processed file');
      // The transfer service has taken the file.
      const isSending = (s: string) => s.includes(STATUS_MESSAGES.SENDING_FILE);
      const isInFlight = (s: string) => isPreparing(s) || isSending(s);

      // Success requires every file to be individually confirmed as exported.
      const hasSuccess = filesStatus.length > 0 && filesStatus.every(isExported);
      // An explicit failure on any file outranks in-flight wording on the others.
      const hasFailure = filesStatus.some(isFailure);
      const hasInProgress = filesStatus.some(isInFlight);
      const hasReachedTransfer = filesStatus.some(isSending);

      const itemDateMs = typeof item.date === 'string' ? new Date(item.date).getTime() : Number(item.date);
      const timeDiff = isNaN(itemDateMs) ? Infinity : Date.now() - itemDateMs;

      // A fresh backend error is itself the write the submit override is waiting
      // for. Without this, a run that failed early - before it wrote any file, so
      // the record date may not advance the way a file write would - stays pinned at
      // Queued for the whole submit window while the failure sits in the tooltip.
      const hasFreshError = this.detailDatedAfterFiles(item) && this.hasErrorDetail(item);

      if (!hasFreshError && this.isAwaitingBackendAfterSubmit(item, itemDateMs)) {
        // Submitted from this session, and the backend has not written anything
        // since. The file statuses still describe the previous run, so reporting
        // them would show the old outcome for as long as the backend takes to
        // start. Hold Queued until there is a fresh write to read.
        this.noteInProgress(item);
        item.status = STATUS_VALUES.QUEUED;
        return item;
      }

      if (hasFreshError) {
        // The backend recorded an error dated after this run's files. The per-file
        // statuses can still read "retrieved" or "exported" from an earlier attempt,
        // so deriving from them alone would show the stale outcome. The dated error
        // is the authoritative signal that this run failed.
        item.status = STATUS_VALUES.UNSUCCESSFUL;
      } else if (hasFailure) {
        // A reported transfer failure is authoritative, whatever the other files say.
        item.status = STATUS_VALUES.UNSUCCESSFUL;
      } else if (hasSuccess) {
        // Loan documents are all clear. Trailing documents are tracked separately
        // and have no retry path, so surface them as a partial success rather
        // than hiding them behind an unqualified Successful.
        item.status = this.failedTrailingDocCount(item) > 0
          ? STATUS_VALUES.PARTIAL_SUCCESS
          : STATUS_VALUES.SUCCESSFUL;
      } else if (hasInProgress) {
        this.noteInProgress(item);

        // Distinguish waiting on Encompass from waiting on the destination, so a
        // slow loan points at the right service.
        const activeStatus = hasReachedTransfer
          ? STATUS_VALUES.TRANSFERRING
          : STATUS_VALUES.RETRIEVING;

        if (timeDiff < TIME_THRESHOLD_MS) {
          item.status = activeStatus;
        } else if (this.isRecentlyActive(item) && !this.hasExceededMaxInProgress(item)) {
          // Past the usual threshold but still visibly moving. Slow destinations
          // are common, so keep waiting rather than reporting a false failure.
          item.status = activeStatus;
        } else {
          // No movement observed, or waited as long as is reasonable.
          item.status = STATUS_VALUES.UNSUCCESSFUL;
        }
      } else if (this.isAwaitingFirstReport(item, timeDiff)) {
        // Accepted but nothing has reported yet. Without this the record would
        // fall through to whatever status it already carried, and a resend clears
        // `files` without clearing `status`, so a new run would display the
        // previous run's result.
        this.noteInProgress(item);
        item.status = STATUS_VALUES.QUEUED;
      } else {
        // Nothing observable to derive from, so keep whatever is stored, normalized.
        // Deliberately not getStatus: that layers derived states on top, and this
        // value is persisted onto the record, so a derived trailing phase written
        // here would be read back after the trailing run finished and the row would
        // report Transferring permanently.
        item.status = this.normalizeStoredStatus(item);
      }

      return item;
    });
  }

  /**
   * Whether a loan was submitted from this session and the backend has not
   * written to it since.
   *
   * The record still holds the previous run's file statuses, so its derived
   * status would describe work that has already finished. Comparing the record's
   * last write against the submission time tells us whether there is anything
   * new to read yet.
   *
   * The override is dropped once the ceiling on in-progress time is passed, so a
   * submission that never produces a backend write cannot pin a row on Queued.
   *
   * @param item - A history record.
   * @param itemDateMs - The record's last write time in milliseconds.
   * @returns True while the row should be held at Queued.
   */
  private isAwaitingBackendAfterSubmit(item: any, itemDateMs: number): boolean {
    const identity = this.recordIdentity(item);
    if (identity === undefined) {
      return false;
    }

    const submittedAt = this.submittedLoans.get(identity);
    if (submittedAt === undefined) {
      return false;
    }

    // Stop overriding once waiting is no longer reasonable.
    if ((Date.now() - submittedAt) > MAX_IN_PROGRESS_MS) {
      this.submittedLoans.delete(identity);
      return false;
    }

    // A write newer than the submission means the backend has started reporting,
    // so the record can speak for itself from here on.
    if (!isNaN(itemDateMs) && itemDateMs >= submittedAt) {
      this.submittedLoans.delete(identity);
      return false;
    }

    return true;
  }

  /**
   * Whether a record has been accepted for processing but has not reported yet.
   *
   * There is no positive signal for this state, only the absence of file
   * statuses, so it is bounded by recency. An old record with no usable file
   * statuses must resolve to a terminal status rather than sitting in progress.
   *
   * @param item - A history record.
   * @param timeDiff - Age of the record's last write, in milliseconds.
   * @returns True when the record is newly submitted and yet to report.
   */
  private isAwaitingFirstReport(item: any, timeDiff: number): boolean {
    const files = item?.files ?? [];
    const hasAnyReportedFile = files.some(
      (f: any) => (f?.statusMessage ?? '').trim().length > 0
    );
    if (hasAnyReportedFile) {
      return false;
    }

    // Either the backend wrote the record recently, or this session just
    // submitted a loan and the first status is still pending.
    const recentlyWritten = timeDiff < TIME_THRESHOLD_MS;
    const recentlySubmitted = this.lastSubmissionAt !== undefined
      && (Date.now() - this.lastSubmissionAt) <= this.POST_SUBMIT_WATCH_MS;

    return recentlyWritten || recentlySubmitted;
  }

  /**
   * Determines if the user is currently viewing the last page in the paginator.
   * Used to conditionally show "No more records" only on the final page.
   * 
   * @returns `true` if on the last page, `false` otherwise.
   */
  isOnLastPage(): boolean {
    if (!this.paginator) return false;
    return this.paginator.pageIndex === this.paginator.getNumberOfPages() - 1;
  }

  /**
   * Triggers a refresh of the history table.
   *
   * - Keeps existing loaded data
   * - Fetches latest records from the beginning
   * - Merges new records with existing cache
   * - Navigates to page 1 to show latest data
   */
  async refresh() {
    if (this.isFetchingHistory) return;
    
    try {
      this.isFetchingHistory = true;
      this.spinnerService.setShowSpinner(true);
      
      console.log('Refreshing: fetching latest records...');
      
      // Fetch from the beginning to get latest records
      const response = await this.dcService.getMoreDocConnectorHistory(
        this.assetService.getSelectedAssetId(),
        undefined  // Start from the beginning
      ) as any;

      if (response.statusFlag && response.content?.Items?.length > 0) {
        const items = this.processHistoryItems(response.content.Items);
        let newRecordsAdded = 0;
        let recordsUpdated = 0;
        
        // Update existing items or add new ones
        items.forEach((newItem: any) => {
          const existingIndex = this.findRecordIndex(this.allHistoryItems, newItem);

          if (existingIndex === -1) {
            // New record - add to beginning
            this.allHistoryItems.unshift(newItem);
            this.totalLoaded++;
            newRecordsAdded++;
          } else {
            // Existing record - update it in place
            this.allHistoryItems[existingIndex] = newItem;
            recordsUpdated++;
          }
        });

        // Push the refreshed copy of the open loan to the detail view, and rebind
        // any checked rows to the new objects.
        //
        // The cached records are replaced with new objects above, so without this
        // the detail view keeps rendering the object captured when the loan was
        // opened: pressing refresh updated the table behind it while the file rows
        // on screen stayed frozen. The background poll already did this; the manual
        // refresh did not.
        //
        // Every processed item is passed rather than only the updated ones, so the
        // open loan is found whichever branch above handled it.
        this.refreshOpenLoanDetail(items);
        this.refreshCheckedLoans(items);
        
        if (newRecordsAdded > 0 || recordsUpdated > 0) {
          console.log(`Refresh: Added ${newRecordsAdded} new records, updated ${recordsUpdated} existing records`);
          
          // Re-sort to maintain order
          if (this.sort && this.sort.active) {
            this.applySortToData();
          } else {
            // Default sort by date descending
            this.allHistoryItems.sort((a, b) => {
              const dateA = typeof a.date === 'string' ? new Date(a.date).getTime() : a.date;
              const dateB = typeof b.date === 'string' ? new Date(b.date).getTime() : b.date;
              return dateB - dateA;
            });
          }
        } else {
          console.log('Refresh: No changes detected');
        }
        
        // Clear pending records since we just refreshed
        this.pendingNewRecords = [];
        this.hasNewRecordsAvailable = false;
        
        // Navigate to page 1 and update display
        this.currentPageIndex = 0;
        const start = 0;
        const end = this.currentPageSize;
        this.dataSource.data = this.allHistoryItems.slice(start, end);
        
        // Update paginator
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.updatePaginator();
      }
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      this.isFetchingHistory = false;
      this.spinnerService.setShowSpinner(false);
    }
  }

  /**
   * Fetches records up to the batch limit (1000) or until end of results.
   * Can be called multiple times to fetch additional batches.
   */
  async fetchToEnd() {
    if (this.isFetchingToEnd || this.isFetchingHistory) {
      console.log('Already fetching, skipping...');
      return;
    }

    // Check if we're filtering - don't allow fetch to end during filtering
    const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;
    if (isFiltering) {
      this.dialog.open(ErrorDialogComponent, {
        data: {
          title: 'Cannot Fetch to End',
          message: 'Please clear the filter before fetching all records.'
        }
      });
      return;
    }

    this.isFetchingToEnd = true;
    this.cancelFetchToEnd = false;
    this.showMoreRecordsAvailable = false;
    this.spinnerService.setShowSpinner(true);

    const startingCount = this.totalLoaded;
    let fetchedInThisBatch = 0;

    try {
      console.log(`Starting fetch to end from ${startingCount} records...`);

      // Fetch until we hit the batch limit or run out of data
      while (!this.isEndOfResults && !this.cancelFetchToEnd && fetchedInThisBatch < this.FETCH_TO_END_BATCH_LIMIT) {
        const response = await this.dcService.getMoreDocConnectorHistory(
          this.assetService.getSelectedAssetId(),
          this.lastEvaluatedKey
        ) as DocConnectorHistoryResponse;

        if (!response.statusFlag) {
          this.handleError(response.statusMessage ?? 'Unknown error');
          break;
        }

        const responseItems = response.content?.Items || [];
        
        if (responseItems.length === 0 && !response.content?.LastEvaluatedKey) {
          this.isEndOfResults = true;
          break;
        }

        const items = this.processHistoryItems(responseItems);
        
        // Add unique items
        items.forEach(newItem => {
          const existingIndex = this.findRecordIndex(this.allHistoryItems, newItem);

          if (existingIndex === -1) {
            this.allHistoryItems.push(newItem);
            this.totalLoaded++;
            fetchedInThisBatch++;
          }
        });

        // Update pagination token
        this.lastEvaluatedKey = response.content.LastEvaluatedKey || null;
        
        if (!this.lastEvaluatedKey) {
          this.isEndOfResults = true;
          this.showMoreRecordsAvailable = false; // Clear flag when we reach true end
          break;
        }

        // Check if we've hit the batch limit
        if (fetchedInThisBatch >= this.FETCH_TO_END_BATCH_LIMIT) {
          console.log(`Batch limit reached (${this.FETCH_TO_END_BATCH_LIMIT} records)`);
          this.showMoreRecordsAvailable = !!this.lastEvaluatedKey;
          break;
        }
      }

      // Apply sort and update display
      if (this.sort && this.sort.active) {
        this.applySortToData();
      } else {
        this.allHistoryItems.sort((a, b) => {
          const dateA = typeof a.date === 'string' ? new Date(a.date).getTime() : a.date;
          const dateB = typeof b.date === 'string' ? new Date(b.date).getTime() : b.date;
          return dateB - dateA;
        });
      }

      // Navigate to the last page
      const lastPageIndex = Math.max(0, Math.ceil(this.totalLoaded / this.currentPageSize) - 1);
      
      // Disconnect paginator for manual pagination (not filtering)
      this.dataSource.paginator = null;
      
      // Update current page index
      this.currentPageIndex = lastPageIndex;
      
      // Display the last page (manual slicing since paginator is disconnected)
      const start = lastPageIndex * this.currentPageSize;
      const end = start + this.currentPageSize;
      this.dataSource.data = this.allHistoryItems.slice(start, end);
      
      // Update paginator using the centralized method (handles showMoreRecordsAvailable)
      this.updatePaginator();
      
      // Trigger change detection to ensure paginator UI updates
      this.cd.detectChanges();

      // Show result message
      if (this.cancelFetchToEnd) {
        console.log(`Fetch cancelled. Loaded ${fetchedInThisBatch} records in this batch.`);
      } else if (this.showMoreRecordsAvailable) {
        this.dialog.open(ErrorDialogComponent, {
          data: {
            title: 'Batch Limit Reached',
            message: `Loaded ${fetchedInThisBatch} records (batch limit: ${this.FETCH_TO_END_BATCH_LIMIT}). More records are available. Click "Last Page" again to fetch the next batch.`
          }
        });
      } else {
        console.log(`Reached end of results. Total loaded: ${this.totalLoaded}`);
      }

    } catch (error) {
      console.error('Error fetching to end:', error);
      this.dialog.open(ErrorDialogComponent, {
        data: {
          title: 'Fetch Error',
          message: 'An error occurred while fetching records.'
        }
      });
    } finally {
      this.isFetchingToEnd = false;
      this.spinnerService.setShowSpinner(false);
    }
  }

  /**
   * Cancels the ongoing fetch-to-end operation
   */
  cancelFetchToEndOperation() {
    if (this.isFetchingToEnd) {
      this.cancelFetchToEnd = true;
      console.log('Cancelling fetch to end...');
    }
  }

  /**
   * Applies pending new records to the main cache and updates the display
   * Only called when user is on page 1 or manually navigates to page 1
   * Also refreshes the display to show any updated records
   */
  private applyPendingNewRecords() {
    // Drop anything the cache already holds. A record is queued as new when the poll
    // cannot find it, but pagination or a search can load that same record before
    // this runs, and unshifting blindly then listed the loan twice. Duplicates share
    // a trackBy key, so Angular bound a row to the wrong record and opening one loan
    // displayed a different one along with its files.
    const newRecords = this.pendingNewRecords.filter(
      (record: any) => this.findRecordIndex(this.allHistoryItems, record) === -1
    );
    const skipped = this.pendingNewRecords.length - newRecords.length;
    if (skipped > 0) {
      console.log(`Skipped ${skipped} pending record(s) already present in the cache`);
    }
    const hasNewRecords = newRecords.length > 0;

    // Cleared whether or not anything was applied, so a batch that turned out to be
    // entirely duplicates is not reconsidered on every later cycle.
    this.pendingNewRecords = [];
    this.hasNewRecordsAvailable = false;

    if (hasNewRecords) {
      console.log(`Applying ${newRecords.length} pending new records`);

      // Add pending records to the beginning
      this.allHistoryItems.unshift(...newRecords);
      this.totalLoaded += newRecords.length;

      // Re-sort to maintain order
      if (this.sort && this.sort.active) {
        this.applySortToData();
      } else {
        // Default sort by date descending
        this.allHistoryItems.sort((a, b) => {
          const dateA = typeof a.date === 'string' ? new Date(a.date).getTime() : a.date;
          const dateB = typeof b.date === 'string' ? new Date(b.date).getTime() : b.date;
          return dateB - dateA;
        });
      }
    }
    
    // Always update display for page 1 (to show both new and updated records)
    const start = 0;
    const end = this.currentPageSize;
    this.dataSource.data = this.allHistoryItems.slice(start, end);
    
    // Update paginator
    this.updatePaginator();
    
    // Trigger change detection to ensure UI updates
    this.cd.detectChanges();
  }
  
  resetActivity = () => {
    this.lastActivityTime = Date.now();
  };


  onSelectLoan(selected: any) {
    this.dcService.sendButtonFlag.next(false);
    this.selectedLoan = selected;
    this.showSelectedLoanView = true;
    this.selectedFieldService.onHistoryLoanFieldSelected.next(selected);
    this.selectedLoanDataSource = new MatTableDataSource(selected.files);
  }

  getData() {
    this.selectedSub = this.selectedFieldService.onHistoryLoanFieldSelected
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(selected => {
        this.selectedLoan = selected;
      });
    // Subscribe to the dcDocument
    this.dcDocSub = this.dcService.dcDocument
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(documentCore => {
        if (documentCore) {
          this.dcDoc = JSON.parse(JSON.stringify(documentCore));
        }
      });
    // Subscribe to the dcDocument
    this.selectedLoansToSendSub = this.selectedFieldService.checkedHistoryLoans
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(historyLoansToSend => {
        if (historyLoansToSend) {
          console.log(historyLoansToSend);
          this.checkedLoans = JSON.parse(JSON.stringify(historyLoansToSend));
        }
      });
    this.spinnerSub = this.spinnerService.spinner
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(spinner => {
        this.showSpinner = spinner;
      });
  }

  clearSelectedLoan() {
    if (this.checkedLoans.length > 0) {
      this.dcService.sendButtonFlag.next(true);
    }
    this.isLoanSelectVisible = false;
    this.showSelectedLoanView = false;
    this.selectedFieldService.onHistoryLoanFieldSelected.next(undefined);
    
    // Don't clear the history filter - it should persist when going back
  }

  async retrieveSettings(): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.http.post(
          `${Buffer.from(environment.environmentURL, 'base64').toString()}/dynamodb/get-document`,
          {
            tableName: 'assets',
            primaryKeyAttribute: 'assetId',
            primaryKey: this.assetService.getSelectedAssetId(),
          },
          this.httpOptions
        ).pipe(
          catchError((error) => {
            this.customErrorHandlerService.handleError(error); 
            throw error; 
          })
        )
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
  
  retryTransfer(data: any, url: string): void {
    this.http.post(
      `${Buffer.from(environment.environmentURL, 'base64').toString()}${url}`,
      data,
      this.httpOptions
    ).pipe(
      catchError((error) => {
        this.customErrorHandlerService.handleError(error); 
        throw error; 
      })
    ).subscribe({
      next: () => {
        console.log('Retry process initiated successfully.');
      },
      error: (error) => {
        console.error('Error occurred while initiating retry:', error);
      }
    });
  }  

  getDocConnectorDocument() {
    this.dcService.dcDocument
    .pipe(takeUntil(this.unsubscribe))
    .subscribe(documentCore => {
      console.log(documentCore);
      if (documentCore) {
        documentCore = documentCore;
        let jsonObject = JSON.parse(JSON.stringify(documentCore));
        delete jsonObject['destinations'];
        delete jsonObject['globalMetas'];
        delete jsonObject['_id'];
        delete jsonObject['_version'];
        jsonObject['_id'] = this.assetService.getSelectedAssetId();
        this.configurations = documentCore.configurations;
      }
    });
  }

  async onRetrySend(event: any) {
      event.preventDefault();
      const sendDocConnectorDialogResponse: any = await this.dialogHandlerService.handleDialog(ResendDocConnectorDialogComponent, {
        data: this.selectedLoan.loanNumber
      })
      console.log({
        sendDocConnectorDialogResponse
      });
      if (!sendDocConnectorDialogResponse.statusFlag) {
        console.log('User canceled send request');
      } else {
        this.spinnerService.setShowSpinner(true);
        const settings = await this.retrieveSettings();
        this.getDocConnectorDocument();
        let coreRequests = [];
        for (const [key, value] of this.checkedFiles.entries()) {
          console.log(JSON.stringify({ key: key, value: value }));

          const fileLocation = value?.S3FileLocation || 
            `${this.assetService.getSelectedAssetId()}/${this.selectedLoan.loanGUID}/${value.destination}/${value.destination}.pdf`;
          const metaLocation = this.replaceLastFilenameWithMeta(value?.S3FileLocation) || 
            `${this.assetService.getSelectedAssetId()}/${this.selectedLoan.loanGUID}/${value.destination}/meta.json`;

          let request = {
            "assetID": this.assetService.getSelectedAssetId(),
            "loanName": this.selectedLoan.loanNumber,
            "fileName": value.fileName,
            "fileType": this.configurations?.fileType || "",
            "metaLocation": metaLocation,
            "fileLocation": fileLocation,
            "configurations": {
              ...this.configurations
            },
            "isRetry": true
          }
          coreRequests.push(request);
        }  
        if (coreRequests.length > 0) {
          const url = `/${settings.coreCompany}/${settings.core}/send-file`;
          if (!settings.core.toLowerCase().includes("centerdoc")) {
            this.retryTransfer(coreRequests, url);
            this.isAllSelectLoanBoxesChecked = false;
            this.dialog.open(SuccessDialogComponent, {
              data: {
                title: 'Success',
                message: 'Retry process initiated successfully.'
              }
            })
          } else {
            this.dialog.open(ErrorDialogComponent, {
              data: {
                message: "Retry transfer feature not available for CenterDoc."
              }
            })
          }
          this.checkedFiles.clear();
        } else {
          this.dialog.open(ErrorDialogComponent, {
            data: {
              title: 'Error',
              message: "No Files Selected"
            }
          })
        }
        console.log("Core Requests", coreRequests);
        this.spinnerService.setShowSpinner(false);
    }
  }


   replaceLastFilenameWithMeta(filePath: string) {
    if (!filePath) {
      return "";
    }
    // Split the path into parts based on '/'
    const pathParts = filePath.split('/');

    // Replace the last part (filename) with 'meta.json'
    pathParts[pathParts.length - 1] = 'meta.json';

    // Rebuild the path
    return pathParts.join('/');
  }

  ngOnDestroy() {
    // Set before anything else so a cycle that is mid-flight stops as soon as it
    // resumes, rather than finishing against a view that no longer exists.
    this.isDestroyed = true;

    if (this.intervalId) {
      // The poll reschedules itself with setTimeout, so it must be cleared as a
      // timeout. Leaving it would keep querying after the view is gone.
      clearTimeout(this.intervalId);
      this.intervalId = undefined;
    }
    window.removeEventListener("mousemove", this.resetActivity.bind(this));
    window.removeEventListener("keydown", this.resetActivity.bind(this));
    document.removeEventListener("visibilitychange", this.resetActivity.bind(this));
    this.selectedFieldService.checkedHistoryLoans.next([]);
    
    // Clear search result cache
    this.searchResultsCache.clear();
    this.clearProgressTracking();
    this.submittedLoans.clear();
    
    this.filterSubject.complete();
    this.unsubscribe.next(0);
    this.unsubscribe.complete();
  }

  updateCheckedLoans(event: any, historyLoan: any) {
    console.log(event);
    if (event.checked) {
      // Only allow to send one loan at a time due to long wait times for computation
      this.checkedLoans = [];
      for (let loan of this.dataSource.data) {
        loan.checkToSend = false;
      }
      historyLoan.checkToSend = true;
      this.checkedLoans.push(historyLoan);
    } else {
      let loanIndex = this.checkedLoans.findIndex(
        (loan: any) => loan.loanGUID === historyLoan.loanGUID
      );
      if (loanIndex > -1) {
        this.checkedLoans.splice(loanIndex, 1);
      }
    }
    this.selectedFieldService.checkedHistoryLoans.next(this.checkedLoans);
    if (this.checkedLoans.length > 0) {
      this.dcService.sendButtonFlag.next(true);
    } else {
      this.dcService.sendButtonFlag.next(false);
    }
    console.log('Checked Loans', this.checkedLoans);
  }

   updateCheckedFiles(updatedFiles: Map<string, any>) {
    this.checkedFiles = updatedFiles;
  }

  resetCheckedFiles(): void {
    this.checkedFiles = new Map<string, any>(); // Reset the map
    this.isAllSelectLoanBoxesChecked = false;
  }

  updateLoanSelectVisibility(bool: boolean) {
    this.isLoanSelectVisible = bool;
  }

  /**
   * Applies filter to the data source and triggers debounced API search for missing records
   * @param filterValue - The search term to filter by
   */
  applyFilter(filterValue: string) {
    const trimmedValue = filterValue.trim().toLowerCase();
    this.dataSource.filter = trimmedValue;
    
    // Trigger debounced search (will fetch all matching records)
    this.filterSubject.next(trimmedValue);
  }

  /**
   * Filters the locally cached data without making API calls
   * Used when all records have already been loaded
   * @param filterValue - The search term to filter by
   */
  private filterLocally(filterValue: string): void {
    console.log(`Filtering ${this.allHistoryItems.length} cached records locally`);
    
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const searchTerm = filter.toLowerCase().trim();
      const loanNumber = (data.loanNumber || '').toLowerCase();
      const loanGUID = (data.loanGUID || '').toLowerCase();
      
      // Match if loanNumber or loanGUID starts with the search term (like API's begins_with)
      if (loanNumber.startsWith(searchTerm) || loanGUID.startsWith(searchTerm)) {
        return true;
      }
      
      // Only try date matching if search term clearly looks like a date (contains / or -)
      // Don't match dates for pure numeric searches (those should only match loan numbers)
      if (searchTerm.includes('/') || searchTerm.includes('-')) {
        try {
          // Convert the record's date (ms timestamp) to a formatted string
          const recordDate = typeof data.date === 'string' ? new Date(data.date) : new Date(data.date);
          
          if (!isNaN(recordDate.getTime())) {
            // Format: M/D/YYYY (e.g., "12/1/2025")
            const formattedDate = `${recordDate.getMonth() + 1}/${recordDate.getDate()}/${recordDate.getFullYear()}`;
            // Format: MM/DD/YYYY (e.g., "12/01/2025")
            const formattedDatePadded = `${String(recordDate.getMonth() + 1).padStart(2, '0')}/${String(recordDate.getDate()).padStart(2, '0')}/${recordDate.getFullYear()}`;
            // Format: YYYY-MM-DD (e.g., "2025-12-01")
            const isoDate = recordDate.toISOString().split('T')[0];
            
            // Check if any date format starts with the search term (not just contains)
            if (formattedDate.startsWith(searchTerm) || 
                formattedDatePadded.startsWith(searchTerm) || 
                isoDate.startsWith(searchTerm)) {
              return true;
            }
          }
        } catch (e) {
          // If date parsing fails, just skip date matching
        }
      }
      
      return false;
    };
    
    // Disconnect paginator FIRST to prevent interference
    this.dataSource.paginator = null;
    
    // Set data to the data source
    this.dataSource.data = [...this.allHistoryItems];
    
    // Apply the filter
    this.dataSource.filter = filterValue;
    
    // Reset paginator and update length BEFORE connecting
    if (this.paginator) {
      const filteredCount = this.dataSource.filteredData.length;
      this.paginator.pageIndex = 0;
      this.paginator.length = filteredCount;
      this.paginator.pageSize = this.currentPageSize;
      
      // If no results, ensure paginator shows 0
      if (filteredCount === 0) {
        this.paginator.length = 0;
      }
    }
    
    // Connect paginator to data source LAST for automatic pagination
    this.dataSource.paginator = this.paginator;
    
    console.log(`Local filter found ${this.dataSource.filteredData.length} matching records`);
  }

  /**
   * Applies current sort to the data
   */
  private applySortToData(): void {
    if (!this.sort || !this.sort.active) return;

    const sortField = this.sort.active;
    const sortDirection = this.sort.direction;
    const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;

    console.log(`Applying sort: ${sortField} ${sortDirection}, isFiltering: ${isFiltering}`);

    // Sort the full dataset
    this.allHistoryItems.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle date field specially
      if (sortField === 'date') {
        aValue = typeof aValue === 'string' ? new Date(aValue).getTime() : aValue;
        bValue = typeof bValue === 'string' ? new Date(bValue).getTime() : bValue;
      }

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    if (isFiltering) {
      // When filtering, let MatTableDataSource handle both filtering and sorting
      this.dataSource.data = [...this.allHistoryItems];
      this.dataSource.paginator = this.paginator;
      console.log(`Filtering active: showing all ${this.allHistoryItems.length} records for MatTableDataSource to filter and paginate`);
    } else {
      // When not filtering, do manual pagination to preserve page position
      this.dataSource.paginator = null;
      const start = this.currentPageIndex * this.currentPageSize;
      const end = start + this.currentPageSize;
      this.dataSource.data = this.allHistoryItems.slice(start, end);
      console.log(`No filtering: showing page ${this.currentPageIndex + 1} (records ${start + 1}-${Math.min(end, this.allHistoryItems.length)})`);
    }
  }

  /**
   * Finds the page index where a specific item would appear based on current sort
   */
  private findPageForItem(item: any): number {
    const index = this.allHistoryItems.findIndex(i => 
      i.loanGUID === item.loanGUID && i.date === item.date
    );
    
    if (index === -1) return 0;
    
    return Math.floor(index / this.currentPageSize);
  }

  /**
   * Handles clearing the filter - keeps all records, sorts by current sort, and navigates to correct page
   */
  private async handleFilterClear(): Promise<void> {
    console.log('Filter cleared, keeping all records and applying current sort');
    
    // Clear search-specific state only (don't touch normal pagination state)
    this.searchResultsCache.clear();
    this.lastApiSearchTerm = '';
    this.searchLimitReached = false;
    this.searchLastEvaluatedKey = undefined; // Reset search pagination key
    
    // Clear the filter
    this.dataSource.filter = '';
    
    // Reset filter predicate to default (search all columns)
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const dataStr = Object.keys(data).reduce((currentTerm: string, key: string) => {
        return currentTerm + (data as { [key: string]: any })[key] + '◬';
      }, '').toLowerCase();
      const transformedFilter = filter.trim().toLowerCase();
      return dataStr.indexOf(transformedFilter) != -1;
    };
    
    // Disconnect paginator from data source for manual pagination
    this.dataSource.paginator = null;
    
    // Apply current sort (or default to date descending)
    if (!this.sort || !this.sort.active) {
      // Default sort by date descending
      this.allHistoryItems.sort((a, b) => {
        const dateA = typeof a.date === 'string' ? new Date(a.date).getTime() : a.date;
        const dateB = typeof b.date === 'string' ? new Date(b.date).getTime() : b.date;
        return dateB - dateA; // Descending order (newest first)
      });
    } else {
      // Apply current sort
      this.applySortToData();
    }
    
    // Navigate back to first page when clearing filter
    const targetPage = 0;
    
    if (this.paginator) {
      this.paginator.pageIndex = targetPage;
      // Use the normal pagination state (lastEvaluatedKey) to determine if more records exist
      const hasMore = this.lastEvaluatedKey !== null && !this.isEndOfResults;
      this.paginator.length = hasMore ? this.totalLoaded + 1 : this.totalLoaded;
    }
    
    // Reload the first page using existing cached data
    await this.getHistory(targetPage, this.currentPageSize, false);
  }



  /**
   * Comprehensive search method that fetches ALL matching records from the API
   * Implements paginated search with caching to retrieve complete result sets
   * @param searchTerm - The loan number or loan GUID to search for
   */
  private async searchAllMatchingRecords(searchTerm: string): Promise<void> {
    // Check cache first
    const cachedResults = this.searchResultsCache.get(searchTerm);
    if (cachedResults) {
      console.log('Using cached search results for:', searchTerm);
      this.applySearchResults(cachedResults, searchTerm);
      return;
    }

    // Prevent concurrent searches
    if (this.isSearching || this.lastApiSearchTerm === searchTerm) {
      console.log('Search already in progress or duplicate search term, skipping...');
      return;
    }

    this.isSearching = true;
    this.spinnerService.setShowSpinner(true);
    this.lastApiSearchTerm = searchTerm;
    this.searchLimitReached = false; // Reset the flag at the start of each search
    this.searchLastEvaluatedKey = undefined; // Reset search pagination key for new search

    let allSearchResults: any[] = [];
    let currentSearchKey: any = undefined;
    let hasMoreResults = true;

    try {
      // Fetch ALL pages of search results (up to limit)
      while (hasMoreResults && allSearchResults.length < this.MAX_SEARCH_RESULTS) {
        const response = await this.dcService.searchDocConnectorHistory(
          this.assetService.getSelectedAssetId(),
          searchTerm,
          50, // Fetch in batches of 50
          currentSearchKey
        ) as DocConnectorHistoryResponse;

        if (response.statusFlag && response.content?.Items?.length > 0) {
          allSearchResults.push(...response.content.Items);
          currentSearchKey = response.content.LastEvaluatedKey;
          hasMoreResults = !!currentSearchKey;

          // Stop if we've reached the limit AND there are more results available
          if (allSearchResults.length >= this.MAX_SEARCH_RESULTS && hasMoreResults) {
            console.warn(`Search result limit reached (${this.MAX_SEARCH_RESULTS}). Some results may not be displayed.`);
            this.searchLimitReached = true; // Set flag to show UI warning only if more results exist
            this.searchLastEvaluatedKey = currentSearchKey; // Save the key in case we want to continue later
            hasMoreResults = false;
          }
        } else {
          hasMoreResults = false;
        }
      }

      // Cache and apply search results
      if (allSearchResults.length > 0) {
        this.searchResultsCache.set(searchTerm, allSearchResults);
        this.applySearchResults(allSearchResults, searchTerm);
      } else {
        console.log('No matching records found in API');
        // Handle empty search results - show empty state
        this.applyEmptySearchResults(searchTerm);
      }
    } catch (error: any) {
      console.error('Error searching for records:', error);
      // Handle errors gracefully - network errors should not show dialogs
      if (!error?.message?.includes('network') && !error?.message?.includes('timeout')) {
        this.dialog.open(ErrorDialogComponent, {
          data: {
            title: 'Search Error',
            message: 'Failed to search for records. Please try again.'
          }
        });
      }
    } finally {
      this.isSearching = false;
      this.spinnerService.setShowSpinner(false);
    }
  }

  /**
   * Helper method to apply search results to the data source
   * @param searchResults - Array of search results from the API
   * @param searchTerm - The search term used
   */
  private applySearchResults(searchResults: any[], searchTerm: string): void {
    const processedItems = this.processHistoryItems(searchResults);
    this.addUniqueItemsToCache(processedItems);
    this.applySortToData();

    // Show ALL items for filtering
    this.dataSource.data = [...this.allHistoryItems];
    this.dataSource.filter = searchTerm;
    
    // Connect paginator to data source for automatic pagination during filtering
    this.dataSource.paginator = this.paginator;

    // Update paginator with filtered count
    this.updatePaginatorForFilter();
  }

  /**
   * Handles empty search results by showing an empty table with proper paginator state
   * @param searchTerm - The search term that returned no results
   */
  private applyEmptySearchResults(searchTerm: string): void {
    console.log(`No results found for search term: "${searchTerm}"`);
    
    // Set empty data
    this.dataSource.data = [];
    this.dataSource.filter = searchTerm;
    
    // Connect paginator to data source
    this.dataSource.paginator = this.paginator;
    
    // Update paginator to show 0 results
    if (this.paginator) {
      this.paginator.length = 0;
      this.paginator.pageIndex = 0;
      this.paginator.pageSize = this.currentPageSize;
    }
  }

  /**
   * Adds unique items to the cache, preventing duplicates
   * @param newItems - Array of new items to add
   */
  private addUniqueItemsToCache(newItems: any[]): void {
    newItems.forEach((newItem: any) => {
      const existingIndex = this.findRecordIndex(this.allHistoryItems, newItem);

      if (existingIndex === -1) {
        this.allHistoryItems.push(newItem);
        this.totalLoaded++;
        return;
      }

      // The same loan, freshly read. Replace the cached copy instead of adding a
      // second entry for it. This previously matched on the record date as well, but
      // the backend rewrites that date on every status update, so a loan already in
      // the list was treated as a different loan and added again. Duplicates share a
      // trackBy key, and Angular then bound a row to the wrong record, which is why
      // opening one loan could display another.
      this.allHistoryItems[existingIndex] = newItem;
    });
  }

  /**
   * Updates paginator based on whether filtering is active
   */
  private updatePaginatorForFilter(): void {
    if (!this.paginator) return;

    const isFiltering = this.dataSource.filter && this.dataSource.filter.length > 0;

    if (isFiltering) {
      // When filtering, show count of filtered results
      this.paginator.length = this.dataSource.filteredData.length;
      this.paginator.pageIndex = 0; // Reset to first page
    } else {
      // When not filtering, show total with "load more" indicator
      const hasMore = this.lastEvaluatedKey !== null;
      this.paginator.length = hasMore ? this.totalLoaded + 1 : this.totalLoaded;
    }

    this.paginator.pageSize = this.currentPageSize;
  }

  isRetry(element: any): boolean {
    const retryTimestampStr = element?.retryTimestamp;
    if (retryTimestampStr) { 
      const retryTimestamp = Number(retryTimestampStr); // Convert string to number
      if (!isNaN(retryTimestamp)) { // Ensure the conversion was successful
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        return retryTimestamp > fiveMinutesAgo; // Return true if it's within the last 5 minutes
      }
    }
    return false; 
  }

  trailingDocFileDispersement(element: any, column: string) {
    let standard = 0;
    let trailing = 0;
    let files = element.files;
    let trailingDocs = element.trailingDocs;
    const historyItemDateInMs = typeof element.date === 'string' ? new Date(element.date).getTime() : element.date;
    const currentTime = new Date().getTime();
    switch(column) {
      case 'processed':
        // Standard processed files
        if (files) {
          standard += files.length;
        }
        trailing = trailingDocsUtil.countProcessed(trailingDocs);
        break;
      case 'transmitted':
        // Standard transmitted files
        if (files && files.length) {
          for (let file of files) {
            if (file?.statusMessage.toLowerCase().includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED)) {
              standard++;
            }
          }
        }
        trailing = trailingDocsUtil.countTransmitted(trailingDocs);
        break;
      case 'pending':
        // Standard pending files
        if (files && files.length) {
          for (let file of files) {
            const statusMessage = file.statusMessage.toLowerCase();
            if  (
              ((statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
              statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
              statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)) && currentTime - historyItemDateInMs < TIME_THRESHOLD_MS) 
              || 
              (this.isRetry(file) && (statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
              statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
              statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)))
            ) {
              standard++;
            }
          }
        }
        trailing = trailingDocsUtil.countPending(trailingDocs, element.date);
        break;
      case 'failed':
        // Standard failed files
        if (files && files.length > 0) {
          for (let file of files) {
            const statusMessage = file.statusMessage.toLowerCase();
            if (
              !statusMessage.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED) &&
              (
                !statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) &&
                !statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) &&
                !statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)
              ) ||

              (
                (statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
                statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
                statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)) && 
                currentTime - historyItemDateInMs > TIME_THRESHOLD_MS &&
                !this.isRetry(file)
              )
            ) {
              standard++;
            }
          }
        }
        // Same definition the Failed column uses, so the tooltip cannot disagree
        // with the number it is explaining.
        trailing = this.failedTrailingDocCount(element);
    }
    return 'Standard: ' + standard.toString() + ` / Trailing: ` + trailing.toString();
  }

  processedFileCount(element: any) {
    // Calculates all the files that have been processed including trailing docs
    let counter = 0;
    if (element.files) {
      counter += element.files.length;
    }
    return counter + trailingDocsUtil.countProcessed(element.trailingDocs);
  }

  transmittedFileCount(element: any) {
    let counter = 0;
    let files = element.files;
    if (files && files.length) {
      for (let file of files) {
        if (file?.statusMessage.toLowerCase().includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED)) {
          counter++;
        }
      }
    }
    return counter + trailingDocsUtil.countTransmitted(element.trailingDocs);
  }

  pendingFileCount(element: any) {
    let counter = 0;
    const files = element.files;
    const historyItemDateInMs = typeof element.date === 'string' ? new Date(element.date).getTime() : element.date;
    const currentTime = new Date().getTime();
    if (files && files.length) {
      for (let file of files) {
        const statusMessage = file.statusMessage.toLowerCase();
        if  (
          ((statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
          statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
          statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)) && currentTime - historyItemDateInMs < TIME_THRESHOLD_MS) 
          || 
          (this.isRetry(file) && (statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
          statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
          statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)))
        ) {
          counter++;
        }
      }
    }
    return counter + trailingDocsUtil.countPending(element.trailingDocs, element.date);
  }

  failedFileCount(element: any) {
    let counter = 0;
    let files = element.files;
    const historyItemDateInMs = typeof element.date === 'string' ? new Date(element.date).getTime() : element.date;
    const currentTime = new Date().getTime();
    if (files && files.length > 0) {
      for (let file of files) {
        const statusMessage = file.statusMessage.toLowerCase();
        if (
          !statusMessage.includes(STATUS_MESSAGES.LOAN_FILE_EXPORTED) &&
          (
            !statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) &&
            !statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) &&
            !statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)
          ) ||

          (
            (statusMessage.includes(STATUS_MESSAGES.DOCUMENT_RETRIEVED) ||
            statusMessage.includes(STATUS_MESSAGES.PROCESSED_FILE) ||
            statusMessage.includes(STATUS_MESSAGES.SENDING_FILE)) && 
            currentTime - historyItemDateInMs > TIME_THRESHOLD_MS &&
            !this.isRetry(file)
          )
        ) {
          counter++;
        }
      }
    }
    // Delegated so the Failed column, the tooltip explaining it, and the detail
    // view share one definition. This block previously read `failed` and
    // `inProcess` only, and treated any inProcess message that was not one of the
    // three in-flight messages as a failure, so a finished document parked in
    // inProcess counted as failed while a genuinely failed one in `processed` did
    // not count at all.
    counter += this.failedTrailingDocCount(element);

    // Removed: `files.status = STATUS_VALUES.UNSUCCESSFUL` on a non-zero count.
    //
    // It assigned a property onto the files *array*, which nothing reads, and threw
    // when a record had trailing failures but no `files`. That was reachable before
    // this change and is more so now that a stalled trailing document counts, so a
    // record with only trailing documents would throw here. This method is called
    // from getStatus for every rendered row, so the throw took out the table.
    return counter;
  }

  /**
   * Maps a trailing document entry to a flat row for CSV export.
   * When the S3 location has enough segments, the source and destination
   * are derived from the folder path rather than the stored values.
   */
  private buildTrailingDocExportRow(content: any) {
    const MIN_S3_PATH_SEGMENTS = 5;
    const pathArray = (content.S3FileLocation ?? '').split('/');
    const derivedLocation =
      pathArray.length >= MIN_S3_PATH_SEGMENTS
        ? `${pathArray[pathArray.length - 3]}/${pathArray[pathArray.length - 2]}`
        : undefined;

    return {
      fileName: content.fileName,
      source: derivedLocation ?? content.source,
      destination: derivedLocation ?? content.destination,
      trailingDate: this.dcService.trailingDates(content.updatedAt),
      statusMessage: content.statusMessage
    };
  }

  exportToCSVStatusReport() {
    const dialogRef = this.dialog.open(CCMCConfirmDialogComponent, {
      data: 'Continue exporting status report to CSV?'
    });
    console.log('export to csv 703');
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        let temp: any = [];
        try {
          if (this.exportButtonSwitch === false) {
            for (let row of this.dataSource.filteredData) {
              temp.push({
                loanNumber: row.loanNumber,
                processed: this.processedFileCount(row),
                transmitted: this.transmittedFileCount(row),
                pending: this.pendingFileCount(row),
                failed: this.failedFileCount(row)
              });
            }
          } else {
            // Standard documents
            for (let row of this.selectedLoan.files) {
              temp.push({
                fileName: row.fileName,
                source: row.source,
                destination: row.destination,
                trailingDate: "",
                statusMessage: row.statusMessage
              });
            }
            const trailingDocs = this.selectedLoan?.trailingDocs;
            if (trailingDocs) {
              // Trailing documents, across every processing state
              const trailingDocGroups = [
                trailingDocs.failed,
                trailingDocs.inProcess,
                trailingDocs.processed
              ];
              for (const group of trailingDocGroups) {
                for (const content of Object.values(group ?? {})) {
                  temp.push(this.buildTrailingDocExportRow(content));
                }
              }
            }
          }
        } catch (error) {
          console.error('Error creating export document', error);
        }

        console.log('Export to CSV');
        this.spinnerService.setShowSpinner(true);
        if (temp) {
          console.log('Document to export', temp);
          let tempDoc = JSON.parse(JSON.stringify(temp));
          for (let value of tempDoc) {
            delete value.edited;
          }
          console.log(tempDoc);
          this.ccmcApiService
            .convertJSONToCSV(tempDoc)
            .subscribe(result => {
              console.log('export to csv 713');
              this.spinnerService.setShowSpinner(false);
              console.log(result);
              if (result) {
                const parsedResult = JSON.parse(JSON.stringify(result));
                if (parsedResult.statusFlag) {
                  var blob = new Blob([parsedResult.content], {
                    type: 'text/csv;charset=utf-8;'
                  });
                  var link = document.createElement('a');
                  if (link.download !== undefined) {
                    // feature detection
                    // Browsers that support HTML5 download attribute
                    var url = URL.createObjectURL(blob);
                    link.setAttribute('href', url);
                    link.setAttribute(
                      'download',
                      `${this.assetService.getSelectedFI()}-${this.assetService.getSelectedAssetId()}-StatusReport-${
                        new Date().toISOString()
                      }.csv`
                    );
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                } else {
                  const errorDialogRef = this.dialog.open(
                    ErrorDialogComponent,
                    {
                      data: {
                        title: 'Export Error',
                        message: parsedResult.statusMessage
                      }
                    }
                  );
                }
              }
            });
        }
      }
    });
  }

  clearSearchData() {
    this.filterSearchRef.nativeElement.value = '';
    // Clear the filter and trigger refresh
    this.applyFilter('');
  }
}
